import { useEffect, useState, useMemo, useRef, type ReactNode } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useStudioStore, useAppStore, useAuthStore } from '@/store'
import { useTelegram } from '@/hooks/useTelegram'
import { getSupabase } from '@/lib/supabase'
import { DailyTotals, StudioSubscriptionType, MetricPeriod } from '@/types'
import { Card } from '@/components/ui'
import { NumberInput, PeriodSelector } from '@/components/ui/FormComponents'
import { RefundsInput } from '@/components/ui/RefundsInput'
import { LoadingScreen } from '@/components/LoadingScreen'
import { RevenueConfirmation } from '@/components/RevenueConfirmation'
import { isCurrentMonth, formatCurrency, getCurrencySymbol } from '@/lib/utils'
import { getLocalIsoDate, getMoscowIsoDate } from '@/lib/analytics/periodCalculations'
import { getStudioPeriodSelection, setStudioPeriodSelection } from '@/lib/studioPeriodSelection'
import { calculateGrossRevenueFromDaily } from '@/lib/revenue'
import { setHasUnsavedMetricsChanges } from '@/lib/unsavedChanges'
import { AlertTriangle, Calendar, CheckCircle, ChevronDown, Clock, Info } from 'lucide-react'
import { BulkMetricModal } from '@/components/BulkMetricModal'
import {
  loadStudioDailyAuthorship,
  type StudioDailyAuthorship,
} from '@/services/studioDailyAuthorshipService'
import { Button } from '@/components/ui/Button'
import { BottomSheet } from '@/components/ui/BottomSheet'

const METRICS_SELECTION_STORAGE_KEY = 'metrics_page_selection_v1'
const POST_MONTH_EDIT_WINDOW_DAYS = 1

function firstNonEmpty<T>(...values: Array<T | null | undefined>): T | null {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== '') return value
  }
  return null
}

function toNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  if (typeof value === 'string') {
    const normalized = value.replace(/\s/g, '').replace(',', '.')
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

function normalizeDateOnly(value: unknown): string | null {
  if (!value) return null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed
    const parsed = new Date(trimmed)
    if (!Number.isNaN(parsed.getTime())) {
      return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`
    }
    return trimmed.slice(0, 10)
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
  }
  return null
}

function parseLocalDateOnly(value: string): Date {
  return new Date(`${value}T00:00:00`)
}

function getCalendarMonthEndIso(dateIso: string): string {
  const date = parseLocalDateOnly(dateIso)
  const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0)
  return getLocalIsoDate(monthEnd)
}

function getMetricEditDeadlineIso(dateIso: string): string {
  const monthEnd = parseLocalDateOnly(getCalendarMonthEndIso(dateIso))
  monthEnd.setDate(monthEnd.getDate() + POST_MONTH_EDIT_WINDOW_DAYS)
  return getLocalIsoDate(monthEnd)
}

function ChannelSection({
  title,
  titleClass,
  badgeClass,
  showTitle,
  children,
}: {
  title: string
  titleClass: string
  badgeClass: string
  showTitle: boolean
  children: ReactNode
}) {
  return (
    <div className="space-y-4">
      {showTitle && (
        <div>
          <h3 className="text-base font-semibold">
            <span className={`inline-flex items-center rounded-md border px-2.5 py-1 ${titleClass} ${badgeClass}`}>
              {title}
            </span>
          </h3>
        </div>
      )}
      <div className="space-y-5">{children}</div>
    </div>
  )
}

function ChannelSubsection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-4">
      <h4 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/60">
        {title}
      </h4>
      <div className="space-y-4">{children}</div>
    </div>
  )
}

const USER_INPUT_METRIC_FIELDS: Array<keyof DailyTotals> = [
  'calls_count',
  'answered_calls_count',
  'marketing_expenses_amount',
  'leads_count',
  'appointments_count',
  'new_visitors_count',
  'trial_payments_amount',
  'new_sales_count',
  'new_sales_amount',
  'renewals_count',
  'renewals_amount',
  'upgrades_count',
  'upgrades_amount',
  'returning_clients_count',
  'returning_clients_amount',
  'membership_fees_new_amount',
  'new_subscriptions_count',
  'new_subscriptions_amount',
  'membership_fees_subscription_upgrades_amount',
  'membership_fees_subscription_returning_amount',
  'subscription_renewals_count',
  'subscription_renewals_amount',
  'subscription_upgrades_count',
  'subscription_upgrades_amount',
  'subscription_returning_clients_count',
  'subscription_returning_clients_amount',
  'private_sessions_count',
  'private_sessions_amount',
  'other_income',
  'refunds_amount'
]

const DAILY_COUNT_FIELD_MAX_DIGITS = 2
const DAILY_COUNT_FIELD_WARNING =
  'Обратите внимание: это количественное поле, сюда можно ввести не больше двух цифр. Для выручки используйте соседнее поле суммы.'

const COUNT_INPUT_PROPS = {
  min: 0,
  inputMode: 'numeric' as const,
}

interface WeeklyRevenuePlanRow {
  week_start: string
  week_end: string
  week_len_days: number | null
  weekly_gross_revenue_plan: number | null
}

interface RevenueSplit {
  memberships: number
  subscriptions: number
}

function calculateAppRevenueSplit(metrics: Partial<DailyTotals>): RevenueSplit {
  const memberships = [
    metrics.new_sales_amount,
    metrics.trial_payments_amount,
    metrics.renewals_amount,
    metrics.upgrades_amount,
    metrics.returning_clients_amount,
    metrics.private_sessions_amount,
    metrics.other_income,
    metrics.refunds_amount
  ].reduce((sum, value) => sum + toNumber(value), 0)

  const subscriptions = [
    metrics.new_subscriptions_amount,
    metrics.membership_fees_new_amount,
    metrics.subscription_renewals_amount,
    metrics.subscription_upgrades_amount,
    metrics.subscription_returning_clients_amount,
    metrics.membership_fees_subscription_upgrades_amount,
    metrics.membership_fees_subscription_returning_amount
  ].reduce((sum, value) => sum + toNumber(value), 0)

  return { memberships, subscriptions }
}

export function MetricsPage() {
  const CRM_MATCH_TOLERANCE_RUB = 1
  const { studioId } = useParams<{ studioId: string }>()
  const navigate = useNavigate()
  const { getUserRole, studios, setStudios } = useStudioStore()
  const { user } = useAuthStore()
  const { currentMonth } = useAppStore()
  const { mainButton, backButton, hapticFeedback, setHeaderColor, setBackgroundColor } = useTelegram()
  
  // Типы и рефы для управления MainButton
  type MBMode = 'hidden' | 'save' | 'confirm'
  const mbModeRef = useRef<MBMode>('hidden')
  const mbClickRef = useRef<'save' | 'confirm' | null>(null)
  const authorshipRequestRef = useRef(0)
  
  const [selectedDate, setSelectedDate] = useState<string>('')
  const [selectedPeriod, setSelectedPeriod] = useState<MetricPeriod | null>(null)
  const [availablePeriods, setAvailablePeriods] = useState<MetricPeriod[]>([])
  const [currentPeriodIndex, setCurrentPeriodIndex] = useState(0)
  const [metrics, setMetrics] = useState<Partial<DailyTotals>>({})
  const [originalMetrics, setOriginalMetrics] = useState<Partial<DailyTotals>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [monthlyData, setMonthlyData] = useState<DailyTotals[]>([])
  const [showRevenueConfirmation, setShowRevenueConfirmation] = useState(false)
  const [showBulkMarketingModal, setShowBulkMarketingModal] = useState(false)
  const [showBulkLeadsModal, setShowBulkLeadsModal] = useState(false)
  const [showDiffSheet, setShowDiffSheet] = useState(false)
  const [countFieldWarning, setCountFieldWarning] = useState<string | null>(null)
  const [needsConfirmation, setNeedsConfirmation] = useState(false)
  const [studioSubscriptionType, setStudioSubscriptionType] = useState<StudioSubscriptionType>('REGULAR')
  const [crmRevenueByDate, setCrmRevenueByDate] = useState<Record<string, number>>({})
  const [crmRevenueSplitByDate, setCrmRevenueSplitByDate] = useState<Record<string, RevenueSplit>>({})
  const [weeklyRevenuePlanRows, setWeeklyRevenuePlanRows] = useState<WeeklyRevenuePlanRow[]>([])
  const [copyReportNotice, setCopyReportNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)
  // Что экран предлагает сделать перед копированием отчета: сохранить правки или подтвердить выручку
  const [copyPromptMode, setCopyPromptMode] = useState<'save' | 'confirm' | null>(null)
  const [showRegularSalesBlock, setShowRegularSalesBlock] = useState(false)
  // Кем и когда день сохранён/подтверждён. Отдельным запросом, потому что
  // времени сохранения в studio_daily_totals нет, а confirmed_by хранит
  // и uuid, и легаси-telegram_id — см. studioDailyAuthorshipService.
  const [authorship, setAuthorship] = useState<StudioDailyAuthorship | null>(null)

  const studioIntId = parseInt(studioId || '0')
  const studioCurrency = useMemo(
    () => studios.find((s) => s.studio_internal_id === studioIntId)?.currency_code || 'RUB',
    [studios, studioIntId]
  )
  const studioCurrencySymbol = useMemo(() => getCurrencySymbol(studioCurrency), [studioCurrency])
  const currentStudioNumber = useMemo(
    () => studios.find((s) => s.studio_internal_id === studioIntId)?.studio_number ?? null,
    [studios, studioIntId]
  )
  const currentPartnerNumber = useMemo(
    () => studios.find((s) => s.studio_internal_id === studioIntId)?.partner_number ?? null,
    [studios, studioIntId]
  )
  const crmStudioNumber = useMemo(() => {
    if (currentStudioNumber === null) return null
    const parsed = Number(currentStudioNumber)
    return Number.isFinite(parsed) ? parsed : null
  }, [currentStudioNumber])
  const userRole = getUserRole(studioIntId)
  const isManagementCompany = userRole === 'management_company'
  
  const hasMetricsEditRole = Boolean(
    !isManagementCompany &&
    userRole &&
    ['member', 'manager', 'owner'].includes(userRole)
  )
  const selectedDateEditDeadline = useMemo(
    () => (selectedDate ? getMetricEditDeadlineIso(selectedDate) : null),
    [selectedDate]
  )
  const isSelectedDateInEditableMonth = useMemo(() => {
    if (!selectedDateEditDeadline) return false
    return getLocalIsoDate() <= selectedDateEditDeadline
  }, [selectedDateEditDeadline])

  const isWithinPostMonthEditWindow = useMemo(() => {
    if (!selectedDate || !selectedDateEditDeadline || !isSelectedDateInEditableMonth) return false

    const today = parseLocalDateOnly(getLocalIsoDate())
    const monthEnd = parseLocalDateOnly(getCalendarMonthEndIso(selectedDate))
    const diffDays = Math.floor((today.getTime() - monthEnd.getTime()) / (1000 * 60 * 60 * 24))

    return diffDays > 0 && diffDays <= POST_MONTH_EDIT_WINDOW_DAYS
  }, [selectedDate, selectedDateEditDeadline, isSelectedDateInEditableMonth])
  
  // Наперёд не вносится и не подтверждается: сервер (RLS) пускает запись только по
  // московское сегодня включительно, экран это зеркалит
  const isSelectedDateInFuture = Boolean(selectedDate) && selectedDate > getMoscowIsoDate()

  const canEdit = hasMetricsEditRole && isSelectedDateInEditableMonth && !isSelectedDateInFuture
  const isOwner = userRole === 'owner'
  // Единое правило экрана: владелец подтверждает выручку самим фактом сохранения;
  // у остальных ролей подтверждение снимает только изменение самой выручки — это
  // решает серверный триггер, клиент поля подтверждения при этом не присылает
  const saveConfirmsRevenue = isOwner && !!user?.id

  // Условия для отображения полей в зависимости от типа подписки
  const showRegularFields = studioSubscriptionType === 'REGULAR' || studioSubscriptionType === 'MIXED'
  const showSubscriptionFields = studioSubscriptionType === 'SUBSCRIPTION' || studioSubscriptionType === 'MIXED'
  const shouldShowRevenueSplit = showSubscriptionFields
  const isMixedMode = studioSubscriptionType === 'MIXED'
  const showChannelTitles = isMixedMode
  
  // Normalize value for comparison (treat null, undefined, empty string as null, but keep 0 as 0)
  // Handle NaN specially since NaN !== NaN
  const normalizeValue = (value: any): number | null => {
    if (value === null || value === undefined || value === '') return null
    const numValue = Number(value)
    // Treat NaN as null for comparison purposes
    return isNaN(numValue) ? null : numValue
  }
  
  const hasUserEnteredData = (data?: Partial<DailyTotals> | null) => {
    if (!data) return false
    return USER_INPUT_METRIC_FIELDS.some((field) => normalizeValue(data[field]) !== null)
  }

  const shouldShowRegularSalesToggle = studioSubscriptionType === 'SUBSCRIPTION'
  const hasRegularSalesData = useMemo(
    () =>
      [
        metrics.new_sales_count,
        metrics.new_sales_amount,
        metrics.renewals_count,
        metrics.renewals_amount,
        metrics.upgrades_count,
        metrics.upgrades_amount,
        metrics.returning_clients_count,
        metrics.returning_clients_amount,
      ].some((value) => {
        const normalized = normalizeValue(value)
        return normalized !== null && normalized !== 0
      }),
    [
      metrics.new_sales_count,
      metrics.new_sales_amount,
      metrics.renewals_count,
      metrics.renewals_amount,
      metrics.upgrades_count,
      metrics.upgrades_amount,
      metrics.returning_clients_count,
      metrics.returning_clients_amount,
    ]
  )

  useEffect(() => {
    if (!countFieldWarning) return
    const timeoutId = window.setTimeout(() => setCountFieldWarning(null), 3500)
    return () => window.clearTimeout(timeoutId)
  }, [countFieldWarning])

  const handleCountFieldInvalidInput = (message: string) => {
    setCountFieldWarning(message)
    hapticFeedback.notification('warning')
  }

  const countInputProps = {
    ...COUNT_INPUT_PROPS,
    maxIntegerDigits: DAILY_COUNT_FIELD_MAX_DIGITS,
    invalidInputMessage: DAILY_COUNT_FIELD_WARNING,
    onInvalidInput: handleCountFieldInvalidInput,
  }
  const shouldRenderRegularFields = showRegularFields || (shouldShowRegularSalesToggle && showRegularSalesBlock)

  // Check if there are any changes compared to original data (только поля, вводимые через приложение)
  const hasChanges = useMemo(() => {
    const changes: any[] = []
    for (const key of USER_INPUT_METRIC_FIELDS) {
      const currentValue = normalizeValue(metrics[key])
      const originalValue = normalizeValue(originalMetrics[key])
      if (currentValue !== originalValue) {
        changes.push({ key, currentValue, originalValue })
      }
    }
    
    if (changes.length > 0) {
      console.log('Detected changes:', changes)
    }
    
    return changes.length > 0
  }, [metrics, originalMetrics])

  const confirmDiscardChanges = () => {
    if (!hasChanges) return true
    return confirm('У вас есть несохраненные изменения. Вы уверены, что хотите выйти без сохранения?')
  }

  // Закрываем шторку если изменения сброшены
  useEffect(() => {
    if (!hasChanges) setShowDiffSheet(false)
  }, [hasChanges])

  useEffect(() => {
    setHasUnsavedMetricsChanges(hasChanges)
    return () => {
      setHasUnsavedMetricsChanges(false)
    }
  }, [hasChanges])

  useEffect(() => {
    if (shouldShowRegularSalesToggle && hasRegularSalesData) {
      setShowRegularSalesBlock(true)
    }
  }, [shouldShowRegularSalesToggle, hasRegularSalesData])

  useEffect(() => {
    if (canEdit) return
    setShowBulkMarketingModal(false)
    setShowBulkLeadsModal(false)
  }, [canEdit])

  // Check if there's any user-entered data to confirm
  const hasDataToConfirm = useMemo(() => {
    return hasUserEnteredData(metrics)
  }, [metrics])
  
  // Check if revenue confirmation is needed (only for owners and when there's data)
  const revenueNeedsConfirmation = isOwner && 
    !metrics.gross_revenue_confirmed &&
    !hasChanges && // Only when there are no pending changes
    hasDataToConfirm // Only when there's actual data to confirm
  // Определяем желаемый режим MainButton
  const desiredMode: MBMode = useMemo(() => {
    if (!canEdit) return 'hidden'
    if (hasChanges) return 'save'
    if (revenueNeedsConfirmation) return 'confirm'
    return 'hidden'
  }, [canEdit, hasChanges, revenueNeedsConfirmation])

  // Функция загрузки данных периода (объявляется до handleSave)
  const loadMonthlyData = async () => {
    if (!selectedPeriod) return
    
    try {
      const startDate = selectedPeriod.period_start
      const endDate = selectedPeriod.period_end
      const supabase = getSupabase()
      
      const { data, error } = await supabase
        .from('studio_daily_totals')
        .select('*')
        .eq('studio_internal_id', studioIntId)
        .gte('metric_date', startDate)
        .lte('metric_date', endDate)
        .order('metric_date')

      if (error) throw error
      
      setMonthlyData(data || [])
    } catch (err) {
      console.error('Error loading period data:', err)
    }
  }

  const loadCrmPeriodData = async () => {
    if (!selectedPeriod || crmStudioNumber === null) {
      setCrmRevenueByDate({})
      setCrmRevenueSplitByDate({})
      return
    }

    try {
      const supabase = getSupabase()
      const response = await (supabase as any)
        .from('report_payments')
        .select('date, studio_number, amount, subscription_sale')
        .eq('studio_number', crmStudioNumber)
        .gte('date', selectedPeriod.period_start)
        .lte('date', selectedPeriod.period_end)

      if (response.error) {
        throw response.error
      }
      const rows: any[] = Array.isArray(response.data) ? response.data : []

      const nextMap: Record<string, number> = {}
      const nextSplitMap: Record<string, RevenueSplit> = {}
      rows.forEach((row) => {
        const metricDateRaw = firstNonEmpty<string>(row.date)
        const metricDate = normalizeDateOnly(metricDateRaw)
        if (!metricDate) return
        if (metricDate < selectedPeriod.period_start || metricDate > selectedPeriod.period_end) return

        const amount = toNumber(row.amount)
        const subscriptionSale = typeof row.subscription_sale === 'string' ? row.subscription_sale.trim() : 'Нет'
        const split = nextSplitMap[metricDate] || { memberships: 0, subscriptions: 0 }

        nextMap[metricDate] = (nextMap[metricDate] || 0) + amount
        if (subscriptionSale === 'Да') {
          split.subscriptions += amount
        } else {
          split.memberships += amount
        }
        nextSplitMap[metricDate] = split
      })

      setCrmRevenueByDate(nextMap)
      setCrmRevenueSplitByDate(nextSplitMap)
    } catch (error: any) {
      console.error('Error loading CRM period data:', error?.message || error)
      setCrmRevenueByDate({})
      setCrmRevenueSplitByDate({})
    }
  }

  const loadWeeklyRevenuePlan = async () => {
    if (!selectedPeriod) {
      setWeeklyRevenuePlanRows([])
      return
    }

    try {
      const supabase = getSupabase()
      const { data, error } = await supabase
        .from('studio_weekly_plan_vs_fact')
        .select('week_start, week_end, week_len_days, weekly_gross_revenue_plan')
        .eq('studio_internal_id', studioIntId)
        .eq('period_id', selectedPeriod.period_id)
        .order('week_start')

      if (error) throw error

      setWeeklyRevenuePlanRows((data || []) as WeeklyRevenuePlanRow[])
    } catch (err) {
      console.error('Error loading weekly revenue plan:', err)
      setWeeklyRevenuePlanRows([])
    }
  }

  // Подпись «кем и когда» (объявляется до handleSave, который её вызывает).
  // Ответ приходит асинхронно, поэтому помечаем запрос номером: при быстром
  // переключении дней ответ по прошлой дате не должен подписать текущую.
  const refreshAuthorship = async () => {
    if (!studioIntId || !selectedDate) {
      setAuthorship(null)
      return
    }

    const requestId = authorshipRequestRef.current + 1
    authorshipRequestRef.current = requestId

    const result = await loadStudioDailyAuthorship(studioIntId, selectedDate)
    if (authorshipRequestRef.current !== requestId) return
    setAuthorship(result)
  }

  // Функция сохранения (объявляется до эффектов)
  // Возвращает true, если данные реально сохранены (нужно для сценария "сохранить и скопировать отчет")
  const handleSave = async (): Promise<boolean> => {
    if (!canEdit || saving) return false

    // Скрываем клавиатуру, убирая фокус с активного элемента
    if (document.activeElement && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }

    try {
      setSaving(true)
      setSaveSuccess(false)
      hapticFeedback.impact('light')

      // Преобразуем пустые строки в null для корректного сохранения в базу.
      // Дальнейшая логика подтверждения теперь окончательно решается на сервере:
      // владелец авто-подтверждает, остальные роли при правке выручки сбрасывают флаг.
      const normalizedMetrics: any = {}
      Object.entries(metrics).forEach(([key, value]) => {
        if (value === '' || value === null || value === undefined) {
          normalizedMetrics[key] = null
        } else {
          normalizedMetrics[key] = value
        }
      })

      // ⚠️ Не-владелец поля подтверждения НЕ ПРИСЫЛАЕТ ВОВСЕ. Снимает подтверждение
      // серверный триггер reset_revenue_confirmation, и только при изменении самой
      // выручки; безусловный `false` от клиента снимал его и при правке маркетинга,
      // лидов или звонков — то есть отменял решение владельца там, где сумма не
      // менялась (Бор 627, 04.09.2026: подтверждено в 13:28, слетело в 13:58).
      const confirmationPayload = saveConfirmsRevenue
        ? {
            gross_revenue_confirmed: true,
            gross_revenue_confirmed_at: new Date().toISOString(),
            gross_revenue_confirmed_by: user.id
          }
        : {}

      const payloadWithConfirmationState = {
        studio_internal_id: studioIntId,
        metric_date: selectedDate,
        ...normalizedMetrics,
        last_saved_by_user_id: user?.id ?? null,
        ...confirmationPayload
      }

      const supabase = getSupabase()

      // Читаем записанную строку: после триггеров подтверждение могло остаться
      // прежним (сумма не менялась) или слететь (сумма изменилась) — угадывать это
      // на клиенте нельзя, иначе экран разойдётся с базой.
      const { data: savedRow, error } = await (supabase as any)
        .from('studio_daily_totals')
        .upsert(payloadWithConfirmationState)
        .select('gross_revenue_confirmed, gross_revenue_confirmed_at, gross_revenue_confirmed_by')
        .maybeSingle()

      if (error) {
        throw error
      }

      setSaveSuccess(true)
      // Обновляем локальное состояние с актуальным статусом подтверждения
      const appliedConfirmation = savedRow
        ? {
            gross_revenue_confirmed: savedRow.gross_revenue_confirmed,
            gross_revenue_confirmed_at: savedRow.gross_revenue_confirmed_at,
            gross_revenue_confirmed_by: savedRow.gross_revenue_confirmed_by
          }
        : confirmationPayload
      const updatedMetrics = {
        ...metrics,
        ...appliedConfirmation
      }
      setMetrics(updatedMetrics)
      setOriginalMetrics(updatedMetrics)
      
      hapticFeedback.notification('success')
      await loadMonthlyData() // Refresh monthly data
      await refreshAuthorship() // Подпись «кем и когда» — со свежими серверными датами

      // Плашка «требуется подтверждение» — по фактическому состоянию после сохранения
      setNeedsConfirmation(updatedMetrics.gross_revenue_confirmed !== true)
      
      // Hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000)

      return true
    } catch (err) {
      hapticFeedback.notification('error')
      return false
    } finally {
      setSaving(false)
    }
  }

  // Функции для управления UI при работе с клавиатурой
  const handleInputFocus = () => {
    // Добавляем класс к body для CSS и скрываем Telegram кнопки
    document.body.classList.add('keyboard-open')
    
    if (window.Telegram?.WebApp) {
      try {
        // Временно скрываем кнопки на время ввода
        mainButton.hide()
        backButton.hide()
      } catch (error) {
        console.log('UI adjustment not supported in this Telegram version')
      }
    }
  }

  const handleInputBlur = () => {
    // Убираем класс и восстанавливаем UI элементы через небольшую задержку
    setTimeout(() => {
      document.body.classList.remove('keyboard-open')
      
      if (window.Telegram?.WebApp) {
        try {
          // Восстанавливаем кнопки в зависимости от состояния
          if (mbModeRef.current !== 'hidden') {
            mainButton.show()
          }
          backButton.show()
        } catch (error) {
          console.log('UI adjustment not supported in this Telegram version')
        }
      }
    }, 300) // Небольшая задержка для стабильности
  }

  useEffect(() => {
    const isEditableInput = (target: EventTarget | null): target is HTMLInputElement | HTMLTextAreaElement => {
      if (!(target instanceof HTMLElement)) return false
      if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return false
      if (target.closest('.tg-header')) return false  // header inputs (partner search etc.) don't trigger keyboard mode
      return !target.disabled && !target.readOnly
    }

    const handleFocusIn = (event: FocusEvent) => {
      if (isEditableInput(event.target)) {
        handleInputFocus()
      }
    }

    const handleFocusOut = (event: FocusEvent) => {
      if (!isEditableInput(event.target)) return

      setTimeout(() => {
        if (!isEditableInput(document.activeElement)) {
          handleInputBlur()
        }
      }, 0)
    }

    document.addEventListener('focusin', handleFocusIn)
    document.addEventListener('focusout', handleFocusOut)

    return () => {
      document.removeEventListener('focusin', handleFocusIn)
      document.removeEventListener('focusout', handleFocusOut)
    }
  }, [])

  // Эффект для определения открытой клавиатуры через изменение viewport
  useEffect(() => {
    let initialHeight = window.innerHeight
    
    const handleResize = () => {
      const currentHeight = window.innerHeight
      const heightDifference = initialHeight - currentHeight
      
      // Если высота экрана уменьшилась больше чем на 150px, считаем что открыта клавиатура
      if (heightDifference > 150) {
        handleInputFocus()
      } else if (heightDifference < 50) {
        handleInputBlur()
      }
    }
    
    window.addEventListener('resize', handleResize)
    
    // Очистка при размонтировании
    return () => {
      window.removeEventListener('resize', handleResize)
      document.body.classList.remove('keyboard-open')
    }
  }, [canEdit, hasChanges, revenueNeedsConfirmation])

  // Load studios if not already loaded
  const loadStudios = async () => {
    if (!user?.id) return
    
    try {
      console.log('🔄 MetricsPage: Loading studios for access check')
      const supabase = getSupabase()
      const { data: studiosData, error: studiosError } = await (supabase as any)
        .rpc('get_my_studios') as {
          data: Array<{
            studio_internal_id: number
            studio_number: string
            city_id: number
            city_name: string
            address: string
            number_of_halls: number
            area: number
            status_code: string
            created_at: string
            updated_at: string
            currency_code?: string | null
            access_kind: string
            team_role: string
          }> | null
          error: any
        }
      
      if (studiosError) {
        console.error('❌ MetricsPage: Error loading studios:', studiosError)
        return
      }
      
      if (studiosData) {
        const processedStudios = studiosData.map((studio: any) => ({
          studio_internal_id: studio.studio_internal_id,
          studio_number: studio.studio_number || `Studio ${studio.studio_internal_id}`,
          city_id: studio.city_id || 0,
          address: studio.address,
          created_at: studio.created_at,
          updated_at: studio.updated_at,
          access_kind: studio.access_kind,
          currency_code: studio.currency_code || 'RUB',
          role: studio.team_role || (studio.access_kind === 'owner' ? 'owner' : 'manager'),
          city: studio.city_name ? { 
            city_id: studio.city_id, 
            city_name: studio.city_name,
            population_thousands: null,
            city_population_group: null,
            city_land_group: null,
            country: 'Россия'
          } : undefined
        }))
        
        console.log('✅ MetricsPage: Studios loaded with access:', processedStudios)
        setStudios(processedStudios)
      }
    } catch (error) {
      console.error('❌ MetricsPage: Error loading studios:', error)
    }
  }

  useEffect(() => {
    // Load studios if we don't have any or if we don't have access to this specific studio
    if (studios.length === 0 || !getUserRole(studioIntId)) {
      loadStudios()
    }
  }, [studioIntId, user?.id])

  // Check if user has access to this studio
  useEffect(() => {
    // Only check access if studios are loaded and we have a valid studio ID
    if (studios.length > 0 && studioIntId && !getUserRole(studioIntId)) {
      console.warn('❌ MetricsPage: User has no access to studio', studioIntId)
      // Redirect to studios page if no access
      navigate('/')
    }
  }, [studios, studioIntId, getUserRole, navigate])

  useEffect(() => {
    if (studioIntId) {
      loadAvailablePeriods()
    }
  }, [studioIntId])

  useEffect(() => {
    if (selectedPeriod && studioIntId) {
      loadMonthlyData()
      loadCrmPeriodData()
      loadWeeklyRevenuePlan()
    }
  }, [selectedPeriod, studioIntId, crmStudioNumber])

  useEffect(() => {
    if (selectedDate && studioIntId) {
      loadMetrics()
    }
  }, [selectedDate, studioIntId, studioSubscriptionType])

  // Пересчитываем gross_revenue когда изменяются метрики
  useEffect(() => {
    if (Object.keys(metrics).length > 0) {
      const calculatedRevenue = calculateGrossRevenueFromDaily(metrics)
      if (metrics.gross_revenue !== calculatedRevenue) {
        setMetrics(prev => ({ ...prev, gross_revenue: calculatedRevenue }))
      }
    }
  }, [metrics.new_sales_amount, metrics.trial_payments_amount, metrics.renewals_amount, 
      metrics.upgrades_amount, metrics.returning_clients_amount, metrics.private_sessions_amount, 
      metrics.other_income, metrics.refunds_amount, metrics.membership_fees_new_amount,
      metrics.new_subscriptions_amount,
      metrics.membership_fees_subscription_upgrades_amount,
      metrics.membership_fees_subscription_returning_amount,
      metrics.subscription_renewals_amount, metrics.subscription_upgrades_amount,
      metrics.subscription_returning_clients_amount, studioSubscriptionType])

  // Загрузка типа подписки студии при монтировании
  useEffect(() => {
    if (studioIntId) {
      loadStudioSubscriptionType()
    }
  }, [studioIntId])

  useEffect(() => {
    if (!window?.Telegram?.WebApp) return
    
    // Скрываем MainButton когда открыто модальное окно подтверждения
    if (showRevenueConfirmation) {
      if (mbClickRef.current) {
        mainButton.offClick(mbClickRef.current === 'save' ? handleSave : () => setShowRevenueConfirmation(true))
        mbClickRef.current = null
      }
      mainButton.hide()
      mbModeRef.current = 'hidden'
      return
    }

    // Если режим не изменился, ничего не делаем
    if (mbModeRef.current === desiredMode) return

    // Снимаем прошлые обработчики при смене режима
    if (mbClickRef.current) {
      mainButton.offClick(mbClickRef.current === 'save' ? handleSave : () => setShowRevenueConfirmation(true))
      mbClickRef.current = null
    }

    switch (desiredMode) {
      case 'hidden': {
        mainButton.hide()
        mainButton.disable()
        mbModeRef.current = 'hidden'
        break
      }
      case 'save': {
        mainButton.setText('Сохранить изменения')
        if (window.Telegram.WebApp?.MainButton) {
          window.Telegram.WebApp.MainButton.color = '#FFC107' // жёлтый
        }
        mainButton.enable()
        mainButton.show()
        mainButton.onClick(handleSave)
        mbClickRef.current = 'save'
        mbModeRef.current = 'save'
        break
      }
      case 'confirm': {
        mainButton.setText('✓ Подтвердить сумму')
        if (window.Telegram.WebApp?.MainButton) {
          window.Telegram.WebApp.MainButton.color = '#22c55e' // зелёный
        }
        mainButton.enable()
        mainButton.show()
        const onConfirm = () => setShowRevenueConfirmation(true)
        mainButton.onClick(onConfirm)
        mbClickRef.current = 'confirm'
        mbModeRef.current = 'confirm'
        break
      }
    }
  }, [desiredMode, showRevenueConfirmation, mainButton, handleSave])

  // Очистка MainButton при размонтировании компонента
  useEffect(() => {
    return () => {
      if (mbClickRef.current) {
        mainButton.offClick(mbClickRef.current === 'save' ? handleSave : () => setShowRevenueConfirmation(true))
        mbClickRef.current = null
      }
      mainButton.hide()
      mbModeRef.current = 'hidden'
    }
  }, [mainButton, handleSave])

  // Предупреждаем при закрытии вкладки / перезагрузке страницы, если есть несохраненные изменения
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasChanges) return
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasChanges])

  // Настройка кнопки "Назад"
  useEffect(() => {
    const handleBack = () => {
      if (!confirmDiscardChanges()) return
      navigate('/') // Возвращаемся на главную страницу со списком студий
    }

    // В Telegram используем BackButton
    backButton.show()
    backButton.onClick(handleBack)

    return () => {
      backButton.hide()
      backButton.offClick(handleBack)
    }
  }, [hasChanges, navigate, backButton])

  const loadMetrics = async () => {
    try {
      setLoading(true)
      const supabase = getSupabase()
      
      const { data, error } = await supabase
        .from('studio_daily_totals')
        .select('*')
        .eq('studio_internal_id', studioIntId)
        .eq('metric_date', selectedDate)
        .single()

      if (error && error.code !== 'PGRST116') { // Not found is OK
        throw error
      }

      const loadedData: Partial<DailyTotals> = data || {}
      // Автоматически пересчитываем общую выручку при загрузке с учетом типа студии
      loadedData.gross_revenue = calculateGrossRevenueFromDaily(loadedData)
      
      setMetrics(loadedData)
      setOriginalMetrics(loadedData) // Сохраняем оригинальные данные для сравнения
      
      // Проверяем, нужно ли подтверждение выручки
      setNeedsConfirmation(
        isOwner && 
        (loadedData.gross_revenue || 0) > 0 && 
        !loadedData.gross_revenue_confirmed
      )
    } catch (err) {
      // Error loading metrics
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refreshAuthorship()
  }, [studioIntId, selectedDate])

  // Загрузка типа подписки студии
  const loadStudioSubscriptionType = async () => {
    try {
      const supabase = getSupabase()
      const { data, error } = await supabase
        .from('studio_subscription_settings')
        .select('subscription_type')
        .eq('studio_internal_id', studioIntId)
        .maybeSingle<{subscription_type: StudioSubscriptionType}>()
      
      if (!error && data) {
        setStudioSubscriptionType(data.subscription_type)
      }
    } catch (err) {
      console.error('Error loading studio subscription type:', err)
      // По умолчанию используется REGULAR
    }
  }
  // Возвращает true, если подтверждение реально записано (нужно для сценария "подтвердить и скопировать отчет")
  const handleConfirmRevenue = async (): Promise<boolean> => {
    if (!user?.id) return false

    // Скрываем клавиатуру, убирая фокус с активного элемента
    if (document.activeElement && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }

    try {
      const supabase = getSupabase()
      
      const { error } = await (supabase as any)
        .from('studio_daily_totals')
        .update({
          gross_revenue_confirmed: true,
          gross_revenue_confirmed_at: new Date().toISOString(),
          gross_revenue_confirmed_by: user.id
        })
        .eq('studio_internal_id', studioIntId)
        .eq('metric_date', selectedDate)

      if (error) throw error

      // Обновляем локальное состояние
      const updatedMetrics = {
        ...metrics,
        gross_revenue_confirmed: true,
        gross_revenue_confirmed_at: new Date().toISOString(),
        gross_revenue_confirmed_by: user.id
      }
      
      setMetrics(updatedMetrics)
      setOriginalMetrics(updatedMetrics)
      setNeedsConfirmation(false)
      setShowRevenueConfirmation(false)
      
      // Обновляем данные календаря чтобы показать подтверждение
      await loadMonthlyData()
      await refreshAuthorship() // Подпись «кем и когда» — со свежими серверными датами

      hapticFeedback.notification('success')

      return true
    } catch (err) {
      console.error('Error confirming revenue:', err)
      hapticFeedback.notification('error')
      return false
    }
  }

  const updateMetric = (field: keyof DailyTotals, value: number | undefined) => {
    setMetrics(prev => ({ ...prev, [field]: value }))
  }

  // Обновляем metrics с автоматическим расчетом gross_revenue
  const updateMetricWithCalculation = (field: keyof DailyTotals, value: number | undefined) => {
    setMetrics(prev => {
      const updated = { ...prev, [field]: value }
      // Автоматически пересчитываем общую выручку с учетом типа студии
      updated.gross_revenue = calculateGrossRevenueFromDaily(updated)
      return updated
    })
  }

  const handleBulkMetricSaved = (field: keyof DailyTotals) => (savedValue?: number) => {
    setMetrics((prev) => ({ ...prev, [field]: savedValue }))
    setOriginalMetrics((prev) => ({ ...prev, [field]: savedValue }))
    void loadMonthlyData()
    void loadMetrics()
    void refreshAuthorship()
  }

  const formatAmountWithSpaces = (value: number) =>
    Math.round(value).toLocaleString('ru-RU').replace(/\u00A0/g, ' ')

  const getFieldLabel = (field: string): string => {
    const labels: Record<string, string> = {
      // Основные метрики активности
      calls_count: 'Количество звонков',
      answered_calls_count: 'Количество дозвонов',
      leads_count: 'Количество лидов',
      appointments_count: 'Количество записей',
      marketing_expenses_amount: 'Расходы на рекламу',
      // Метрики по новым клиентам (REGULAR)
      new_visitors_count: 'Приходы новых',
      new_sales_count: 'Продажи абонементов новым / доплаты (кол-во)',
      new_sales_amount: 'Выручка по новым / доплатам',
      trial_payments_amount: 'Выручка от пробных',
      // Метрики по продлениям (REGULAR)
      renewals_count: 'Продления абонементов (кол-во)',
      renewals_amount: 'Выручка от продлений абонементов',
      // Метрики по апгрейдам (REGULAR)
      upgrades_count: 'Апгрейды абонементов (кол-во)',
      upgrades_amount: 'Выручка от апгрейдов абонементов',
      // Метрики по возвращенным клиентам (REGULAR)
      returning_clients_count: 'Продажи абонементов БЧС (кол-во)',
      returning_clients_amount: 'Выручка от продаж абонементов БЧС',
      // Метрики по новым клиентам (SUBSCRIPTION)
      membership_fees_new_amount: 'Членские взносы новых клиентов',
      new_subscriptions_count: 'Новых подписок (кол-во)',
      new_subscriptions_amount: 'Выручка по подпискам',
      // Метрики по действующим клиентам (SUBSCRIPTION)
      membership_fees_subscription_upgrades_amount: 'Членские взносы при переходах на подписку',
      membership_fees_subscription_returning_amount: 'Членские взносы БЧС подписок',
      subscription_renewals_count: 'Автопродления подписок (кол-во)',
      subscription_renewals_amount: 'Автопродления подписок (сумма)',
      subscription_upgrades_count: 'Переходов с абонемента на подписку (кол-во)',
      subscription_upgrades_amount: 'Выручка от переходов с абонемента на подписку',
      // Метрики по возвращенным клиентам (SUBSCRIPTION)
      subscription_returning_clients_count: 'Продажи подписок БЧС (кол-во)',
      subscription_returning_clients_amount: 'Выручка от продаж подписок БЧС',
      // Метрики по индивидуальным занятиям
      private_sessions_count: 'Блоки индивов (кол-во)',
      private_sessions_amount: 'Выручка от индивов (сумма)',
      // Прочие метрики
      other_income: 'Прочая выручка',
      refunds_amount: 'Возвраты (сумма)',
      // Итоговые метрики
      gross_revenue: 'Общая выручка',
      total_clients_count: 'Общее количество клиентов',
      active_subscribers_count: 'Количество подписок'
    }
    return labels[field] || field
  }

  const getDefaultDateForPeriod = (period: MetricPeriod): string => {
    const today = getLocalIsoDate()
    if (today < period.period_start) return period.period_start
    if (today > period.period_end) return period.period_end
    return today
  }

  // Загрузка доступных периодов
  const loadAvailablePeriods = async () => {
    try {
      const supabase = getSupabase()
      const today = getLocalIsoDate()
      const sharedPeriodId = getStudioPeriodSelection(studioIntId)
      const rawSavedSelection = sessionStorage.getItem(METRICS_SELECTION_STORAGE_KEY)
      const savedSelection: { periodId?: number; date?: string } | null = rawSavedSelection
        ? (() => {
            try {
              return JSON.parse(rawSavedSelection)
            } catch {
              return null
            }
          })()
        : null
      
      const { data, error } = await supabase
        .from('metric_periods')
        .select('*')
        .order('period_start', { ascending: true }) as { data: MetricPeriod[] | null, error: any }
      
      if (error) {
        console.error('Error loading available periods:', error)
        return
      }
      
      if (data && data.length > 0) {
        setAvailablePeriods(data)

        const sharedIndex = typeof sharedPeriodId === 'number'
          ? data.findIndex((p) => p.period_id === sharedPeriodId)
          : -1
        const savedIndex = typeof savedSelection?.periodId === 'number'
          ? data.findIndex((p) => p.period_id === savedSelection.periodId)
          : -1
        const currentIndex = data.findIndex((p) => p.period_start <= today && today <= p.period_end)
        const selectedIndex = sharedIndex >= 0
          ? sharedIndex
          : savedIndex >= 0
          ? savedIndex
          : (currentIndex >= 0 ? currentIndex : data.length - 1)
        const nextSelectedPeriod = data[selectedIndex]
        const savedDate = savedSelection?.date
        const nextSelectedDate = savedDate &&
          savedDate >= nextSelectedPeriod.period_start &&
          savedDate <= nextSelectedPeriod.period_end
          ? savedDate
          : getDefaultDateForPeriod(nextSelectedPeriod)

        setCurrentPeriodIndex(selectedIndex)
        setSelectedPeriod(nextSelectedPeriod)
        setSelectedDate(nextSelectedDate)
        setStudioPeriodSelection(studioIntId, nextSelectedPeriod.period_id)
      }
    } catch (err) {
      console.error('Error loading available periods:', err)
    }
  }

  const handlePreviousPeriod = () => {
    if (currentPeriodIndex <= 0) return
    if (!confirmDiscardChanges()) return
    const nextIndex = currentPeriodIndex - 1
    const nextPeriod = availablePeriods[nextIndex]
    if (!nextPeriod) return

    setCurrentPeriodIndex(nextIndex)
    setSelectedPeriod(nextPeriod)
    setSelectedDate(getDefaultDateForPeriod(nextPeriod))
  }

  const handleNextPeriod = () => {
    if (currentPeriodIndex >= availablePeriods.length - 1) return
    if (!confirmDiscardChanges()) return
    const nextIndex = currentPeriodIndex + 1
    const nextPeriod = availablePeriods[nextIndex]
    if (!nextPeriod) return

    setCurrentPeriodIndex(nextIndex)
    setSelectedPeriod(nextPeriod)
    setSelectedDate(getDefaultDateForPeriod(nextPeriod))
  }

  const canGoNext = () => {
    if (currentPeriodIndex >= availablePeriods.length - 1) return false
    const nextPeriod = availablePeriods[currentPeriodIndex + 1]
    const today = getLocalIsoDate()
    return !!nextPeriod && nextPeriod.period_start <= today
  }

  useEffect(() => {
    if (!selectedPeriod || !selectedDate) return
    sessionStorage.setItem(
      METRICS_SELECTION_STORAGE_KEY,
      JSON.stringify({
        periodId: selectedPeriod.period_id,
        date: selectedDate
      })
    )
    setStudioPeriodSelection(studioIntId, selectedPeriod.period_id)
  }, [selectedPeriod, selectedDate, studioIntId])

  // Получить все даты в периоде
  const getDatesInPeriod = () => {
    if (!selectedPeriod) return []
    
    const dates: string[] = []
    const start = new Date(selectedPeriod.period_start)
    const end = new Date(selectedPeriod.period_end)
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split('T')[0])
    }
    
    return dates
  }

  const hasDataForDate = (date: string) => {
    return monthlyData.some((item) => item.metric_date === date && hasUserEnteredData(item))
  }

  const getRevenueStatusForDate = (date: string) => {
    const dayData = monthlyData.find(item => item.metric_date === date)
    
    if (!dayData || !hasUserEnteredData(dayData)) return 'none' // Нет пользовательских данных
    
    // Если есть данные (независимо от выручки), проверяем статус подтверждения
    // Подтверждение относится ко всему дню, а не только к выручке
    return dayData.gross_revenue_confirmed === true ? 'confirmed' : 'unconfirmed'
  }

  const hasCrmForSelectedDate = selectedDate
    ? Object.prototype.hasOwnProperty.call(crmRevenueByDate, selectedDate)
    : false
  const crmRevenueForSelectedDate = hasCrmForSelectedDate ? (crmRevenueByDate[selectedDate] || 0) : null
  const crmRevenueSplitForSelectedDate = selectedDate
    ? (crmRevenueSplitByDate[selectedDate] || { memberships: 0, subscriptions: 0 })
    : { memberships: 0, subscriptions: 0 }
  const appRevenueSplitForSelectedDate = useMemo(
    () => calculateAppRevenueSplit(metrics),
    [
      metrics.new_sales_amount,
      metrics.trial_payments_amount,
      metrics.renewals_amount,
      metrics.upgrades_amount,
      metrics.returning_clients_amount,
      metrics.new_subscriptions_amount,
      metrics.membership_fees_new_amount,
      metrics.subscription_renewals_amount,
      metrics.subscription_upgrades_amount,
      metrics.subscription_returning_clients_amount,
      metrics.membership_fees_subscription_upgrades_amount,
      metrics.membership_fees_subscription_returning_amount
    ]
  )
  const appRevenueForSelectedDate = metrics.gross_revenue || 0
  const crmDeltaForSelectedDate = appRevenueForSelectedDate - (crmRevenueForSelectedDate ?? 0)
  const shouldShowRevenueComparison = hasDataToConfirm || hasCrmForSelectedDate

  // Подпись «кем и когда» под итогом дня. Имя и время всегда из одного события
  // (см. get_studio_daily_authorship): у сохранения времени в таблице нет, а
  // у подтверждения колонка `gross_revenue_confirmed_at` заполняется браузером,
  // поэтому серверную дату из RPC ставим первой, а колонку держим фолбэком —
  // на случай дней до старта аудита (2026-05-08) и окружений без этой RPC.
  const formatAuthorshipMoment = (value: string | null | undefined) => {
    if (!value) return null
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return null
    return parsed.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const savedAuthorLabel = authorship?.savedByName ?? null
  const savedAtLabel = formatAuthorshipMoment(authorship?.savedAt)
  const confirmedAuthorLabel = authorship?.confirmedByName ?? authorship?.confirmedByRaw ?? null
  const confirmedAtLabel = formatAuthorshipMoment(
    authorship?.confirmedAt ?? metrics.gross_revenue_confirmed_at
  )
  const hasConfirmedAuthorship = Boolean(
    metrics.gross_revenue_confirmed && (confirmedAuthorLabel || confirmedAtLabel)
  )
  const savedAndConfirmedBySameAuthor =
    Boolean(savedAuthorLabel && confirmedAuthorLabel) &&
    (authorship?.savedByUserId && authorship?.confirmedByUserId
      ? authorship.savedByUserId === authorship.confirmedByUserId
      : savedAuthorLabel === confirmedAuthorLabel)
  const savedAndConfirmedTogether =
    hasConfirmedAuthorship &&
    savedAndConfirmedBySameAuthor &&
    savedAtLabel !== null &&
    confirmedAtLabel !== null &&
    savedAtLabel === confirmedAtLabel

  const calendarSummary = useMemo(() => {
    if (!selectedPeriod) {
      return {
        filledDays: 0,
        confirmedDays: 0,
        unconfirmedFilledDays: 0,
        missedDays: 0,
        crmMismatchDays: 0
      }
    }

    const dates = getDatesInPeriod()
    const todayIso = getLocalIsoDate()
    const yesterday = new Date(todayIso + 'T00:00:00')
    yesterday.setDate(yesterday.getDate() - 1)
    const yesterdayIso = getLocalIsoDate(yesterday)
    const maxDateForSummary = selectedPeriod.period_end < yesterdayIso ? selectedPeriod.period_end : yesterdayIso
    const datesToYesterday = dates.filter((date) => date <= maxDateForSummary)

    let filledDays = 0
    let confirmedDays = 0
    let crmMismatchDays = 0

    datesToYesterday.forEach((date) => {
      const dayData = monthlyData.find((item) => item.metric_date === date)
      const hasUserData = hasUserEnteredData(dayData)
      if (hasUserData) {
        filledDays += 1
      }
      if (hasUserData && dayData?.gross_revenue_confirmed === true) {
        confirmedDays += 1
      }

      const hasCrmData = Object.prototype.hasOwnProperty.call(crmRevenueByDate, date)
      if (!hasCrmData) return
      const appRevenue = dayData?.gross_revenue || 0
      const crmRevenue = crmRevenueByDate[date] || 0
      if (Math.abs(appRevenue - crmRevenue) > CRM_MATCH_TOLERANCE_RUB) {
        crmMismatchDays += 1
      }
    })

    const unconfirmedFilledDays = Math.max(0, filledDays - confirmedDays)
    const missedDays = Math.max(0, datesToYesterday.length - filledDays)

    return { filledDays, confirmedDays, unconfirmedFilledDays, missedDays, crmMismatchDays }
  }, [selectedPeriod, monthlyData, crmRevenueByDate])

  const calendarActionText = useMemo(() => {
    if (calendarSummary.missedDays > 0 && calendarSummary.unconfirmedFilledDays > 0) {
      return 'Заполните пропущенные дни и подтвердите внесенные данные.'
    }
    if (calendarSummary.missedDays > 0) {
      return 'Заполните пропущенные дни.'
    }
    if (calendarSummary.unconfirmedFilledDays > 0) {
      return 'Подтвердите заполненные дни.'
    }
    return 'Отлично: все данные заполнены и подтверждены.'
  }, [calendarSummary])

  const currentStudio = useMemo(
    () => studios.find((studio) => studio.studio_internal_id === studioIntId) ?? null,
    [studios, studioIntId]
  )

  const formatReportDate = (dateString: string) => {
    if (!dateString) return ''
    const [year, month, day] = dateString.split('-')
    if (!year || !month || !day) return dateString
    return `${day}.${month}.${year}`
  }

  const formatReportCount = (value: number | string | null | undefined) => {
    const normalized = toNumber(value)
    return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(normalized)
  }

  const formatReportCurrency = (value: number | string | null | undefined) => {
    const normalized = toNumber(value)
    return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(normalized)} ${studioCurrencySymbol}`
  }

  const overlapDaysInclusive = (startA: string, endA: string, startB: string, endB: string) => {
    const start = new Date(`${startA}T00:00:00`)
    const end = new Date(`${endA}T00:00:00`)
    const rangeStart = new Date(`${startB}T00:00:00`)
    const rangeEnd = new Date(`${endB}T00:00:00`)
    const overlapStart = new Date(Math.max(start.getTime(), rangeStart.getTime()))
    const overlapEnd = new Date(Math.min(end.getTime(), rangeEnd.getTime()))
    const diffMs = overlapEnd.getTime() - overlapStart.getTime()
    if (diffMs < 0) return 0
    return Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1
  }

  const reportPlanSummary = useMemo(() => {
    if (!selectedPeriod || !selectedDate) {
      return {
        monthlyPlan: 0,
        plannedToDate: 0,
        actualToDate: 0,
        completionPct: 0,
        requiredPerDay: 0
      }
    }

    const monthlyPlan = weeklyRevenuePlanRows.reduce(
      (sum, row) => sum + toNumber(row.weekly_gross_revenue_plan),
      0
    )

    const plannedToDate = weeklyRevenuePlanRows.reduce((sum, row) => {
      const overlapDays = overlapDaysInclusive(
        row.week_start,
        row.week_end,
        selectedPeriod.period_start,
        selectedDate
      )
      if (overlapDays <= 0) return sum

      const weekLength = row.week_len_days || 7
      const ratio = overlapDays / weekLength
      return sum + toNumber(row.weekly_gross_revenue_plan) * ratio
    }, 0)

    // Выбранный день берем из формы, а не из monthlyData: он может быть отредактирован
    // и еще не сохранен (или сохранен, но monthlyData перезагружается асинхронно)
    const actualToDate = monthlyData
      .filter((day) => day.metric_date >= selectedPeriod.period_start && day.metric_date < selectedDate)
      .reduce((sum, day) => sum + toNumber(day.gross_revenue), 0)
      + (selectedDate >= selectedPeriod.period_start ? toNumber(metrics.gross_revenue) : 0)

    const completionPct = monthlyPlan > 0
      ? (actualToDate / monthlyPlan) * 100
      : 0

    const remainingPlan = Math.max(0, monthlyPlan - actualToDate)
    const remainingDays = overlapDaysInclusive(
      selectedDate,
      selectedPeriod.period_end,
      selectedDate,
      selectedPeriod.period_end
    ) - 1

    return {
      monthlyPlan,
      plannedToDate,
      actualToDate,
      completionPct,
      requiredPerDay: remainingDays > 0 ? remainingPlan / remainingDays : 0
    }
  }, [selectedPeriod, selectedDate, weeklyRevenuePlanRows, monthlyData, metrics.gross_revenue])

  const buildReportHeader = () => {
    const parts = [
      formatReportDate(selectedDate),
      currentStudio?.city?.city_name?.trim() || '',
      currentStudio?.address?.trim() || ''
    ].filter(Boolean)

    if (parts.length <= 1) return parts.join(' ')
    const [datePart, cityPart, addressPart] = parts
    if (addressPart) {
      return `${datePart} ${cityPart}, ${addressPart}`.trim()
    }
    return `${datePart} ${cityPart}`.trim()
  }

  const getReportFields = () => {
    const fields: Array<{ label: string; value: string }> = [
      { label: 'Звонков', value: formatReportCount(metrics.calls_count) },
      { label: 'Лидов', value: formatReportCount(metrics.leads_count) },
      { label: 'Записей', value: formatReportCount(metrics.appointments_count) },
      { label: 'Приходы новых', value: formatReportCount(metrics.new_visitors_count) },
      { label: 'Выручка от пробных', value: formatReportCurrency(metrics.trial_payments_amount) }
    ]

    if (showRegularFields || hasRegularSalesData) {
      fields.push(
        { label: 'Продажи абонементов новым / доплаты', value: formatReportCount(metrics.new_sales_count) },
        { label: 'Выручка по новым / доплатам', value: formatReportCurrency(metrics.new_sales_amount) },
        { label: 'Продления абонементов', value: formatReportCount(metrics.renewals_count) },
        { label: 'Выручка по продлениям абонементов', value: formatReportCurrency(metrics.renewals_amount) },
        { label: 'Апгрейды абонементов', value: formatReportCount(metrics.upgrades_count) },
        { label: 'Выручка по апгрейдам абонементов', value: formatReportCurrency(metrics.upgrades_amount) },
        { label: 'Продажи абонементов БЧС', value: formatReportCount(metrics.returning_clients_count) },
        { label: 'Выручка от продаж абонементов БЧС', value: formatReportCurrency(metrics.returning_clients_amount) }
      )
    }

    if (showSubscriptionFields) {
      fields.push(
        { label: 'Новых подписок', value: formatReportCount(metrics.new_subscriptions_count) },
        { label: 'Выручка по подпискам', value: formatReportCurrency(metrics.new_subscriptions_amount) },
        { label: 'Членские взносы новых клиентов', value: formatReportCurrency(metrics.membership_fees_new_amount) },
        { label: 'Автопродления подписок', value: formatReportCount(metrics.subscription_renewals_count) },
        { label: 'Выручка по автопродлениям подписок', value: formatReportCurrency(metrics.subscription_renewals_amount) },
        { label: 'Переходов с абонемента на подписку', value: formatReportCount(metrics.subscription_upgrades_count) },
        { label: 'Выручка по переходам с абонемента на подписку', value: formatReportCurrency(metrics.subscription_upgrades_amount) },
        { label: 'Продажи подписок БЧС', value: formatReportCount(metrics.subscription_returning_clients_count) },
        { label: 'Выручка от продаж подписок БЧС', value: formatReportCurrency(metrics.subscription_returning_clients_amount) },
        { label: 'Членские взносы при переходах', value: formatReportCurrency(metrics.membership_fees_subscription_upgrades_amount) },
        { label: 'Членские взносы БЧС', value: formatReportCurrency(metrics.membership_fees_subscription_returning_amount) }
      )
    }

    fields.push(
      { label: 'Индивидуальные занятия', value: formatReportCount(metrics.private_sessions_count) },
      { label: 'Выручка от индивидуальных занятий', value: formatReportCurrency(metrics.private_sessions_amount) },
      { label: 'Прочие доходы', value: formatReportCurrency(metrics.other_income) },
      { label: 'Возвраты', value: formatReportCurrency(metrics.refunds_amount) }
    )

    return fields
  }

  const buildDailyReportText = () => {
    const lines = [
      buildReportHeader(),
      '',
      `План на месяц: ${formatReportCurrency(reportPlanSummary.monthlyPlan)}`,
      `Нужно выручки в день: ${formatReportCurrency(reportPlanSummary.requiredPerDay)}`,
      `% выполнения: ${Math.round(reportPlanSummary.completionPct)}%`,
      '',
      ...getReportFields().map((field) => `${field.label}: ${field.value}`),
      '',
      `Количество клиентов: ${formatReportCount(metrics.total_clients_count)}`,
      `Выручка общая за день: ${formatReportCurrency(metrics.gross_revenue)}`
    ]

    return lines.join('\n')
  }

  const regularSalesSummary = useMemo(() => {
    const count =
      toNumber(metrics.new_sales_count) +
      toNumber(metrics.renewals_count) +
      toNumber(metrics.upgrades_count) +
      toNumber(metrics.returning_clients_count)
    const amount =
      toNumber(metrics.new_sales_amount) +
      toNumber(metrics.renewals_amount) +
      toNumber(metrics.upgrades_amount) +
      toNumber(metrics.returning_clients_amount)

    if (count === 0 && amount === 0) return null
    return `• ${count} оп. • ${formatCurrency(amount, studioCurrency)}`
  }, [
    metrics.new_sales_count,
    metrics.renewals_count,
    metrics.upgrades_count,
    metrics.returning_clients_count,
    metrics.new_sales_amount,
    metrics.renewals_amount,
    metrics.upgrades_amount,
    metrics.returning_clients_amount,
    studioCurrency,
  ])

  const copyReportTextToClipboard = async (reportText: string) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(reportText)
      return
    }

    const textArea = document.createElement('textarea')
    textArea.value = reportText
    textArea.style.position = 'fixed'
    textArea.style.opacity = '0'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
  }

  const showCopyReportNotice = (kind: 'success' | 'error', text: string) => {
    setCopyReportNotice({ kind, text })
    hapticFeedback.notification(kind)
    window.setTimeout(() => setCopyReportNotice(null), 4000)
  }

  const copyDailyReport = async () => {
    try {
      await copyReportTextToClipboard(buildDailyReportText())
      return true
    } catch (error) {
      console.error('Error copying daily report:', error)
      return false
    }
  }

  const handleCopyDailyReport = async () => {
    // Отчет строится из того, что сейчас в форме. Если экран чего-то ждет от пользователя
    // (тот же признак, что и у MainButton), спрашиваем — сделать это вместе с копированием
    // или просто скопировать
    if (desiredMode === 'save' || desiredMode === 'confirm') {
      setCopyPromptMode(desiredMode)
      return
    }

    if (await copyDailyReport()) {
      showCopyReportNotice('success', 'Текст отчета скопирован')
    } else {
      showCopyReportNotice('error', 'Не удалось скопировать отчет')
    }
  }

  const handleSaveAndCopyDailyReport = async () => {
    setCopyPromptMode(null)

    // Копируем до сохранения: текст отчета от сохранения не меняется,
    // а буфер обмена в мобильных браузерах доступен только внутри жеста пользователя
    const copied = await copyDailyReport()
    const saved = await handleSave()

    if (!copied) {
      showCopyReportNotice(
        'error',
        saved
          ? 'Показатели сохранены, но скопировать отчет не удалось'
          : 'Не удалось сохранить показатели и скопировать отчет'
      )
      return
    }

    if (saved) {
      showCopyReportNotice(
        'success',
        saveConfirmsRevenue
          ? 'Показатели сохранены, выручка подтверждена, текст отчета скопирован'
          : 'Показатели сохранены, текст отчета скопирован'
      )
    } else {
      showCopyReportNotice('error', 'Отчет скопирован, но сохранить показатели не удалось')
    }
  }

  const handleConfirmAndCopyDailyReport = async () => {
    setCopyPromptMode(null)

    // Тот же порядок, что и в "сохранить и скопировать": подтверждение текст отчета не меняет
    const copied = await copyDailyReport()
    const confirmed = await handleConfirmRevenue()

    if (!copied) {
      showCopyReportNotice(
        'error',
        confirmed
          ? 'Выручка подтверждена, но скопировать отчет не удалось'
          : 'Не удалось подтвердить выручку и скопировать отчет'
      )
      return
    }

    if (confirmed) {
      showCopyReportNotice('success', 'Выручка подтверждена, текст отчета скопирован')
    } else {
      showCopyReportNotice('error', 'Отчет скопирован, но подтвердить выручку не удалось')
    }
  }

  const handleCopyWithoutPromptedAction = async () => {
    setCopyPromptMode(null)

    if (await copyDailyReport()) {
      showCopyReportNotice('success', 'Текст отчета скопирован')
    } else {
      showCopyReportNotice('error', 'Не удалось скопировать отчет')
    }
  }

  if (loading) {
    return <LoadingScreen />
  }

  return (
    <div className="space-y-3">
      {selectedPeriod && (
        <PeriodSelector
          period={selectedPeriod}
          onPrevious={handlePreviousPeriod}
          onNext={handleNextPeriod}
          canGoNext={canGoNext()}
        />
      )}

      {/* Calendar Overview */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5" />
          <h2 className="text-lg font-medium">Календарь показателей</h2>
        </div>
        
        {/* Легенда календаря */}
        <div className="flex flex-wrap gap-2 mb-4 text-xs">
          <div className="px-2 py-1 rounded bg-muted/40 border border-border flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-green-100 border border-green-300" />
            <span>Подтверждено: {calendarSummary.confirmedDays} дней</span>
          </div>
          <div className="px-2 py-1 rounded bg-muted/40 border border-border flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-primary/20 border border-primary/30" />
            <span>Заполнено: {calendarSummary.unconfirmedFilledDays} дней</span>
          </div>
          <div className="px-2 py-1 rounded bg-muted/40 border border-border flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-rose-100 border border-rose-300" />
            <span>Пропущено: {calendarSummary.missedDays} дней</span>
          </div>
          <div className="px-2 py-1 rounded bg-muted/40 border border-border flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm border border-rose-400/80 bg-transparent" />
            <span className={calendarSummary.crmMismatchDays > 0 ? 'text-rose-700' : 'text-foreground'}>
              Расхождения с CRM: {calendarSummary.crmMismatchDays} дней
            </span>
          </div>
        </div>
        <div className="mb-4 text-xs text-muted-foreground">{calendarActionText}</div>
        
        <div className="grid grid-cols-7 gap-1 text-center">
          {/* Дни недели начинаются с четверга */}
          {['Чт', 'Пт', 'Сб', 'Вс', 'Пн', 'Вт', 'Ср'].map(day => (
            <div key={day} className="p-2 text-sm font-medium text-muted-foreground">
              {day}
            </div>
          ))}
          
          {selectedPeriod && (() => {
            const dates = getDatesInPeriod()
            const firstDate = new Date(dates[0])
            // День недели первой даты (0 = воскресенье, 1 = понедельник, ..., 6 = суббота)
            let dayOfWeek = firstDate.getDay()
            // Преобразуем в систему где четверг = 0
            // четверг = 4, пятница = 5, суббота = 6, воскресенье = 0, понедельник = 1, вторник = 2, среда = 3
            let offset = (dayOfWeek + 3) % 7 // четверг становится 0
            
            return (
              <>
                {/* Пустые ячейки до первого дня периода */}
                {Array.from({ length: offset }).map((_, i) => (
                  <div key={`empty-${i}`} className="p-2"></div>
                ))}
                
                {/* Дни периода */}
                {dates.map(date => {
                  const dateObj = new Date(date)
                  const day = dateObj.getDate()
                  const hasData = hasDataForDate(date)
                  const revenueStatus = getRevenueStatusForDate(date)
                  const appRevenue = monthlyData.find(item => item.metric_date === date)?.gross_revenue || 0
                  const hasCrmData = Object.prototype.hasOwnProperty.call(crmRevenueByDate, date)
                  const crmRevenue = hasCrmData ? crmRevenueByDate[date] : null
                  const crmDelta = hasCrmData && crmRevenue !== null ? appRevenue - crmRevenue : null
                  const isCrmMatched = hasCrmData && crmDelta !== null && Math.abs(crmDelta) <= CRM_MATCH_TOLERANCE_RUB
                  const hasCrmMismatch = hasCrmData && !isCrmMatched
                  const isSelected = date === selectedDate
                  const isToday = date === getLocalIsoDate()
                  const isFutureDay = date > getMoscowIsoDate()

                  // Определяем цвет фона в зависимости от статуса
                  let backgroundClass = 'hover:bg-muted'
                  let textClass = ''
                  let borderClass = ''
                  
                  if (isSelected) {
                    // Выбранный день - всегда показываем как выбранный
                    backgroundClass = 'bg-primary'
                    textClass = 'text-primary-foreground'
                  } else if (hasData) {
                    // Дни с данными - проверяем статус подтверждения в первую очередь
                    if (revenueStatus === 'confirmed') {
                      // Подтверждено (любая выручка, включая 0) - светло-зеленый
                      backgroundClass = 'bg-green-100'
                      textClass = 'text-green-700'
                    } else {
                      // Не подтверждено - светло-голубой
                      backgroundClass = 'bg-primary/20'
                      textClass = 'text-primary'
                    }
                  }
                  
                  // Сегодняшний день получает тонкую серую рамку поверх основного цвета
                  if (isToday && !isSelected) {
                    borderClass = 'border border-muted-foreground/40'
                  }

                  // Будущий день открыть можно, но вносить в него нельзя — приглушаем
                  if (isFutureDay && !isSelected && !hasData) {
                    textClass = 'text-muted-foreground/60'
                  }
                  
                  const crmMismatchFrameClass = hasCrmMismatch
                    ? (isSelected ? 'ring-1 ring-rose-400/80 ring-inset' : 'border border-rose-400/70')
                    : ''

                  return (
                    <button
                      key={date}
                      onClick={() => {
                        if (date === selectedDate) return
                        if (!confirmDiscardChanges()) return
                        setSelectedDate(date)
                      }}
                      className={`
                        relative p-2 text-sm rounded-lg transition-colors
                        ${backgroundClass} ${textClass} ${borderClass} ${crmMismatchFrameClass}
                      `}
                      title={
                        hasCrmData && crmRevenue !== null
                          ? `App: ${formatCurrency(appRevenue, studioCurrency)} | CRM: ${formatCurrency(crmRevenue, studioCurrency)} | Δ: ${(crmDelta || 0).toLocaleString('ru-RU')} ${studioCurrencySymbol}`
                          : 'CRM: 0'
                      }
                    >
                      {day}
                    </button>
                  )
                })}
              </>
            )
          })()}
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm font-medium">Отчет за выбранный день для чата "Показатели"</div>
            <div className="text-xs text-muted-foreground">
              Скопирует текст отчета с планом, % выполнения и всеми полями за {formatReportDate(selectedDate)}
            </div>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={handleCopyDailyReport} disabled={saving}>
            Скопировать отчет
          </Button>
        </div>
        {copyReportNotice && (
          <div
            className={`mt-3 rounded-lg border px-3 py-2 text-sm ${
              copyReportNotice.kind === 'success'
                ? 'border-green-200 bg-green-50 text-green-800'
                : 'border-rose-200 bg-rose-50 text-rose-800'
            }`}
          >
            {copyReportNotice.text}
          </div>
        )}
      </Card>

      <BottomSheet
        isOpen={copyPromptMode !== null}
        onClose={() => setCopyPromptMode(null)}
        title={copyPromptMode === 'confirm' ? 'Выручка не подтверждена' : 'Есть несохраненные изменения'}
      >
        <div className="px-4 py-4 space-y-4">
          {copyPromptMode === 'confirm' ? (
            <p className="text-sm text-muted-foreground">
              Показатели за {formatReportDate(selectedDate)} сохранены, но сумма выручки еще не подтверждена.
              Подтвердить ее вместе с копированием отчета?
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Отчет за {formatReportDate(selectedDate)} будет скопирован с теми значениями, которые сейчас
                в форме, — они еще не сохранены в базе. Сохранить их вместе с копированием?
              </p>
              {saveConfirmsRevenue && (
                <p className="text-sm text-muted-foreground">
                  Как и кнопка «Сохранить изменения» на экране, сохранение сразу подтвердит выручку за день.
                </p>
              )}
            </>
          )}
          <div className="flex flex-col gap-2 sm:flex-row">
            {copyPromptMode === 'confirm' ? (
              <>
                <button
                  type="button"
                  onClick={handleCopyWithoutPromptedAction}
                  className="flex-1 btn-secondary"
                >
                  Скопировать
                </button>
                <button
                  type="button"
                  onClick={handleConfirmAndCopyDailyReport}
                  className="flex-1 btn-primary"
                >
                  Подтвердить и скопировать
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setCopyPromptMode(null)}
                  className="flex-1 btn-secondary"
                >
                  Продолжить редактирование
                </button>
                <button
                  type="button"
                  onClick={handleSaveAndCopyDailyReport}
                  disabled={saving}
                  className="flex-1 btn-primary disabled:opacity-50"
                >
                  {saving
                    ? 'Сохранение…'
                    : saveConfirmsRevenue
                      ? 'Сохранить, подтвердить и скопировать'
                      : 'Сохранить и скопировать'}
                </button>
              </>
            )}
          </div>
        </div>
      </BottomSheet>

      {/* Sticky unsaved-changes banner */}
      {hasChanges && canEdit && (
        <div
          className="sticky z-20 flex items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 shadow-sm"
          style={{ top: 'var(--header-height, 0px)' }}
        >
          <span className="text-sm font-medium text-amber-800">Есть несохранённые изменения</span>
          <button
            type="button"
            onClick={() => setShowDiffSheet(true)}
            /* ⚠️ Заливка на ролях, а не на лестнице: пара `bg-amber-400
               text-amber-950` в тёмной теме давала 2,07:1 — текст зеркалится
               в почти-белый, а `hover:bg-amber-500` самозеркальный и остаётся
               ярко-жёлтым. Роли не зеркалятся вовсе. */
            className="shrink-0 rounded-md bg-primary px-3 py-1 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Посмотреть →
          </button>
        </div>
      )}

      {countFieldWarning && (
        <div
          className="fixed inset-x-4 bottom-[calc(88px+env(safe-area-inset-bottom))] z-50 rounded-2xl border-2 border-orange-400 bg-orange-50 px-4 py-3 text-sm text-orange-950 shadow-[0_16px_40px_rgba(0,0,0,0.22)]"
          role="alert"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-orange-600" />
            <div className="font-medium leading-5">
              {countFieldWarning}
            </div>
          </div>
        </div>
      )}

      {/* Sticky confirm-revenue banner */}
      {isOwner && !hasChanges && revenueNeedsConfirmation && (
        <div
          className="sticky z-20 flex items-center justify-between gap-2 rounded-lg border border-green-300 bg-green-50 px-3 py-2 shadow-sm"
          style={{ top: 'var(--header-height, 0px)' }}
        >
          <span className="text-sm font-medium text-green-800">Выручка не подтверждена</span>
          <button
            type="button"
            onClick={() => setShowRevenueConfirmation(true)}
            className="shrink-0 rounded-md bg-green-500 px-3 py-1 text-sm font-medium text-white transition-colors hover:bg-fixed-green-600"
          >
            Подтвердить →
          </button>
        </div>
      )}

      {/* Bottom sheet: diff + сохранение */}
      {showDiffSheet && (
        <div
          className="fixed inset-0 z-50 flex flex-col justify-end sm:items-center sm:justify-center sm:px-4 sm:py-6"
          onClick={() => setShowDiffSheet(false)}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full bg-card rounded-t-2xl shadow-xl max-h-[70vh] flex flex-col animate-slide-up sm:max-w-2xl sm:rounded-2xl sm:animate-none"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Ручка */}
            <div className="flex justify-center pt-3 pb-1 shrink-0 sm:hidden">
              <div className="w-10 h-1 rounded-full bg-border" />
            </div>

            {/* Заголовок */}
            <div className="px-4 pb-3 shrink-0">
              <h3 className="text-base font-medium">Несохранённые изменения</h3>
            </div>

            {/* Скроллируемый контент */}
            <div className="px-4 overflow-y-auto flex-1 space-y-4 pb-2">

              {/* Изменения */}
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Изменения</p>
                <div className="space-y-0.5">
                  {Object.entries(metrics).filter(([key, value]) => {
                    if (key === 'gross_revenue') return false
                    return normalizeValue(value) !== normalizeValue((originalMetrics as any)[key])
                  }).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between py-1 text-sm">
                      <span className="text-muted-foreground">{getFieldLabel(key)}</span>
                      <span className="tabular-nums font-medium">
                        {normalizeValue((originalMetrics as any)[key]) != null && (
                          <><span className="text-muted-foreground/60 font-normal">{normalizeValue((originalMetrics as any)[key])}</span>{' → '}</>
                        )}
                        {normalizeValue(value) ?? '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Детализация выручки */}
              {shouldShowRevenueComparison && (
                <div className="p-3 bg-muted/20 rounded-lg border border-muted">
                  <div className="text-xs text-muted-foreground mb-2 font-medium">Детализация расчета выручки:</div>
                  <div className="space-y-1 text-xs tabular-nums">
                    {(metrics.new_sales_amount || 0) > 0 && <div className="flex justify-between"><span>Новые продажи:</span><span>+{formatAmountWithSpaces(metrics.new_sales_amount!)}</span></div>}
                    {(metrics.trial_payments_amount || 0) > 0 && <div className="flex justify-between"><span>Выручка от пробных:</span><span>+{formatAmountWithSpaces(metrics.trial_payments_amount!)}</span></div>}
                    {(metrics.new_subscriptions_amount || 0) > 0 && <div className="flex justify-between"><span>Новые подписки:</span><span>+{formatAmountWithSpaces(metrics.new_subscriptions_amount!)}</span></div>}
                    {(metrics.membership_fees_new_amount || 0) > 0 && <div className="flex justify-between"><span>Членские взносы новых клиентов:</span><span>+{formatAmountWithSpaces(metrics.membership_fees_new_amount!)}</span></div>}
                    {(metrics.renewals_amount || 0) > 0 && <div className="flex justify-between"><span>Продления абонементов:</span><span>+{formatAmountWithSpaces(metrics.renewals_amount!)}</span></div>}
                    {(metrics.upgrades_amount || 0) > 0 && <div className="flex justify-between"><span>Апгрейды абонементов:</span><span>+{formatAmountWithSpaces(metrics.upgrades_amount!)}</span></div>}
                    {(metrics.returning_clients_amount || 0) > 0 && <div className="flex justify-between"><span>Продажи абонементов БЧС:</span><span>+{formatAmountWithSpaces(metrics.returning_clients_amount!)}</span></div>}
                    {(metrics.subscription_renewals_amount || 0) > 0 && <div className="flex justify-between"><span>Автопродления подписок:</span><span>+{formatAmountWithSpaces(metrics.subscription_renewals_amount!)}</span></div>}
                    {(metrics.subscription_upgrades_amount || 0) > 0 && <div className="flex justify-between"><span>Переходы с абонемента на подписку:</span><span>+{formatAmountWithSpaces(metrics.subscription_upgrades_amount!)}</span></div>}
                    {(metrics.subscription_returning_clients_amount || 0) > 0 && <div className="flex justify-between"><span>Продажи подписок БЧС:</span><span>+{formatAmountWithSpaces(metrics.subscription_returning_clients_amount!)}</span></div>}
                    {(metrics.membership_fees_subscription_upgrades_amount || 0) > 0 && <div className="flex justify-between"><span>Членские взносы при переходах:</span><span>+{formatAmountWithSpaces(metrics.membership_fees_subscription_upgrades_amount!)}</span></div>}
                    {(metrics.membership_fees_subscription_returning_amount || 0) > 0 && <div className="flex justify-between"><span>Членские взносы БЧС:</span><span>+{formatAmountWithSpaces(metrics.membership_fees_subscription_returning_amount!)}</span></div>}
                    {(metrics.private_sessions_amount || 0) > 0 && <div className="flex justify-between"><span>Индивидуальные:</span><span>+{formatAmountWithSpaces(metrics.private_sessions_amount!)}</span></div>}
                    {(metrics.other_income || 0) > 0 && <div className="flex justify-between"><span>Прочие:</span><span>+{formatAmountWithSpaces(metrics.other_income!)}</span></div>}
                    {(metrics.refunds_amount || 0) !== 0 && <div className="flex justify-between text-red-600"><span>Возвраты:</span><span>{formatAmountWithSpaces(metrics.refunds_amount!)}</span></div>}
                    {shouldShowRevenueSplit && (
                      <div className="border-t border-muted pt-2 mt-3 space-y-1">
                        <div className="flex justify-between">
                          <span>Итого по абонементам:</span>
                          <span>{formatAmountWithSpaces(appRevenueSplitForSelectedDate.memberships)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Итого по подпискам:</span>
                          <span>{formatAmountWithSpaces(appRevenueSplitForSelectedDate.subscriptions)}</span>
                        </div>
                      </div>
                    )}
                    <div className={`flex justify-between items-baseline font-semibold text-sm ${shouldShowRevenueSplit ? 'pt-1' : 'border-t border-muted pt-1 mt-3'}`}>
                      <span>Итого:</span>
                      <span className="font-bold tracking-[0.01em]">{formatAmountWithSpaces(appRevenueForSelectedDate)}</span>
                    </div>
                    {(hasCrmForSelectedDate && crmRevenueForSelectedDate !== null) && (
                      <>
                        {shouldShowRevenueSplit && (
                          <div className="pt-3 mt-2 space-y-1">
                            <div className="flex justify-between">
                              <span>Listok CRM по абонементам:</span>
                              <span>{formatAmountWithSpaces(crmRevenueSplitForSelectedDate.memberships)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Listok CRM по подпискам:</span>
                              <span>{formatAmountWithSpaces(crmRevenueSplitForSelectedDate.subscriptions)}</span>
                            </div>
                          </div>
                        )}
                        <div className={`flex justify-between items-baseline font-semibold text-sm ${shouldShowRevenueSplit ? 'pt-1' : 'pt-3 mt-2'}`}>
                          <span>Итого Listok CRM:</span>
                          <span className="font-bold tracking-[0.01em]">{formatAmountWithSpaces(crmRevenueForSelectedDate)}</span>
                        </div>
                      </>
                    )}
                    {(!hasCrmForSelectedDate || crmRevenueForSelectedDate === null) && (
                      <div className={`flex justify-between items-baseline font-semibold text-sm ${shouldShowRevenueSplit ? 'pt-1' : 'pt-3 mt-2'}`}>
                        <span>Итого Listok CRM:</span>
                        <span className="font-bold tracking-[0.01em]">{formatAmountWithSpaces(0)}</span>
                      </div>
                    )}
                    {shouldShowRevenueComparison && (
                      <div className={`flex justify-between ${crmDeltaForSelectedDate === 0 ? 'text-green-700' : 'text-rose-700'}`}>
                        <span>Отклонение от CRM:</span>
                        <span>{crmDeltaForSelectedDate >= 0 ? '+' : ''}{formatAmountWithSpaces(crmDeltaForSelectedDate)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>

            {/* Кнопки */}
            <div className="px-4 pt-3 pb-6 flex gap-2 shrink-0 border-t border-border mt-2">
              <button
                type="button"
                onClick={() => setShowDiffSheet(false)}
                className="flex-1 btn-secondary"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={() => { setShowDiffSheet(false); handleSave() }}
                disabled={saving}
                className="flex-1 btn-primary disabled:opacity-50"
              >
                {saving ? 'Сохранение…' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Metrics Form */}
      <Card>
        <h2 className="text-lg font-medium mb-4">
          Показатели за {new Date(selectedDate).toLocaleDateString('ru-RU')}
        </h2>
        
        {!canEdit && (
          <div className={`border rounded-lg p-3 mb-4 ${
            isManagementCompany 
              ? 'bg-blue-50 border-blue-200' 
              : 'bg-muted/50 border-border'
          }`}>
            <p className={`text-sm ${
              isManagementCompany 
                ? 'text-blue-800' 
                : 'text-muted-foreground'
            }`}>
              {isManagementCompany 
                ? '📊 Режим просмотра управляющей компании. Редактирование метрик недоступно.'
                : !hasMetricsEditRole
                  ? 'У вас нет прав на редактирование'
                  : isSelectedDateInFuture
                    ? 'Этот день ещё не наступил. Показатели вносятся и подтверждаются не раньше самого дня (по московскому времени).'
                  : selectedDateEditDeadline
                    ? `Календарный месяц закрыт. Редактирование было доступно до ${formatReportDate(selectedDateEditDeadline)} включительно.`
                    : 'Редактирование возможно только для открытого календарного месяца'}
            </p>
          </div>
        )}
        {canEdit && isWithinPostMonthEditWindow && selectedDateEditDeadline && (
          <div className="border rounded-lg p-3 mb-4 bg-amber-50 border-amber-200">
            <p className="text-sm text-amber-800">
              {`Редактирование календарного месяца доступно до ${formatReportDate(selectedDateEditDeadline)} включительно.`}
            </p>
          </div>
        )}

        <div className="space-y-6">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <NumberInput
                  label="Расходы на рекламу"
                  value={metrics.marketing_expenses_amount}
                  onChange={(value) => updateMetric('marketing_expenses_amount', value)}
                  disabled={!canEdit}
                  inputMode="decimal"
                  min={0}
                />
                {canEdit && studios.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setShowBulkMarketingModal(true)}
                    className="mt-1.5 text-xs text-primary underline underline-offset-2 hover:opacity-75 transition-opacity"
                  >
                    Внести расход на несколько студий
                  </button>
                )}
              </div>
              <div>
                <NumberInput
                  label="Лиды"
                  value={metrics.leads_count}
                  onChange={(value) => updateMetric('leads_count', value)}
                  disabled={!canEdit}
                  min={0}
                />
                {canEdit && studios.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setShowBulkLeadsModal(true)}
                    className="mt-1.5 text-xs text-primary underline underline-offset-2 hover:opacity-75 transition-opacity"
                  >
                    Внести лиды на несколько студий
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <NumberInput
                label="Количество звонков"
                value={metrics.calls_count}
                onChange={(value) => updateMetric('calls_count', value)}
                disabled={!canEdit}
                min={0}
              />
              <NumberInput
                label="Дозвоны"
                value={metrics.answered_calls_count}
                onChange={(value) => updateMetric('answered_calls_count', value)}
                disabled={!canEdit}
                min={0}
              />
            </div>
          </div>

          {isMixedMode ? (
            <>
              {/* Воронка пробного */}
              <div>
                <h3 className="mb-3 text-base font-semibold text-muted-foreground">Воронка пробного</h3>
                <div className="space-y-4">
                  <NumberInput
                    label="Записи"
                    value={metrics.appointments_count}
                    onChange={(value) => updateMetric('appointments_count', value)}
                    disabled={!canEdit}
                    {...countInputProps}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <NumberInput
                      label="Приходы новых"
                      value={metrics.new_visitors_count}
                      onChange={(value) => updateMetric('new_visitors_count', value)}
                      disabled={!canEdit}
                      {...countInputProps}
                    />
                    <NumberInput
                      label="Выручка от пробных"
                      value={metrics.trial_payments_amount}
                      onChange={(value) => updateMetricWithCalculation('trial_payments_amount', value)}
                      disabled={!canEdit}
                      inputMode="decimal"
                      min={0}
                    />
                  </div>
                </div>
              </div>

              {showRegularFields && (
                <ChannelSection
                  title="Абонементы"
                  titleClass="text-amber-700"
                  badgeClass="border-amber-200 bg-amber-50/80"
                  showTitle={showChannelTitles}
                >
                  <ChannelSubsection title="Новые клиенты">
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput
                        label="Продажи абонементов новым / доплаты"
                        value={metrics.new_sales_count}
                        onChange={(value) => updateMetric('new_sales_count', value)}
                        disabled={!canEdit}
                        {...countInputProps}
                      />
                      <NumberInput
                        label="Выручка по новым / доплатам"
                        value={metrics.new_sales_amount}
                        onChange={(value) => updateMetricWithCalculation('new_sales_amount', value)}
                        disabled={!canEdit}
                        inputMode="decimal"
                        min={0}
                      />
                    </div>
                  </ChannelSubsection>

                  <ChannelSubsection title="Действующие и бывшие клиенты">
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput
                        label="Продления абонементов"
                        value={metrics.renewals_count}
                        onChange={(value) => updateMetric('renewals_count', value)}
                        disabled={!canEdit}
                        {...countInputProps}
                      />
                      <NumberInput
                        label="Выручка"
                        value={metrics.renewals_amount}
                        onChange={(value) => updateMetricWithCalculation('renewals_amount', value)}
                        disabled={!canEdit}
                        inputMode="decimal"
                        min={0}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput
                        label="Апгрейды абонементов"
                        value={metrics.upgrades_count}
                        onChange={(value) => updateMetric('upgrades_count', value)}
                        disabled={!canEdit}
                        {...countInputProps}
                      />
                      <NumberInput
                        label="Выручка"
                        value={metrics.upgrades_amount}
                        onChange={(value) => updateMetricWithCalculation('upgrades_amount', value)}
                        disabled={!canEdit}
                        inputMode="decimal"
                        min={0}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput
                        label="Продажи абонементов БЧС"
                        value={metrics.returning_clients_count}
                        onChange={(value) => updateMetric('returning_clients_count', value)}
                        disabled={!canEdit}
                        {...countInputProps}
                      />
                      <NumberInput
                        label="Выручка"
                        value={metrics.returning_clients_amount}
                        onChange={(value) => updateMetricWithCalculation('returning_clients_amount', value)}
                        disabled={!canEdit}
                        inputMode="decimal"
                        min={0}
                      />
                    </div>
                  </ChannelSubsection>
                </ChannelSection>
              )}

              {showSubscriptionFields && (
                <ChannelSection
                  title="Подписки"
                  titleClass="text-sky-700"
                  badgeClass="border-sky-200 bg-sky-50/80"
                  showTitle={showChannelTitles}
                >
                  <ChannelSubsection title="Новые клиенты">
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput
                        label="Новых подписок"
                        value={metrics.new_subscriptions_count}
                        onChange={(value) => updateMetric('new_subscriptions_count', value)}
                        disabled={!canEdit}
                        {...countInputProps}
                      />
                      <NumberInput
                        label="Выручка по подпискам"
                        value={metrics.new_subscriptions_amount}
                        onChange={(value) => updateMetricWithCalculation('new_subscriptions_amount', value)}
                        disabled={!canEdit}
                        inputMode="decimal"
                        min={0}
                      />
                    </div>

                    <NumberInput
                      label="Членские взносы новых клиентов"
                      value={metrics.membership_fees_new_amount}
                      onChange={(value) => updateMetricWithCalculation('membership_fees_new_amount', value)}
                      disabled={!canEdit}
                      inputMode="decimal"
                      min={0}
                    />
                  </ChannelSubsection>

                  <ChannelSubsection title="Действующие и бывшие клиенты">
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput
                        label="Автопродления подписок"
                        value={metrics.subscription_renewals_count}
                        onChange={(value) => updateMetric('subscription_renewals_count', value)}
                        disabled={!canEdit}
                        {...countInputProps}
                      />
                      <NumberInput
                        label="Выручка"
                        value={metrics.subscription_renewals_amount}
                        onChange={(value) => updateMetricWithCalculation('subscription_renewals_amount', value)}
                        disabled={!canEdit}
                        inputMode="decimal"
                        min={0}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput
                        label="Переходов с абонемента на подписку"
                        value={metrics.subscription_upgrades_count}
                        onChange={(value) => updateMetric('subscription_upgrades_count', value)}
                        disabled={!canEdit}
                        {...countInputProps}
                      />
                      <NumberInput
                        label="Выручка"
                        value={metrics.subscription_upgrades_amount}
                        onChange={(value) => updateMetricWithCalculation('subscription_upgrades_amount', value)}
                        disabled={!canEdit}
                        inputMode="decimal"
                        min={0}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput
                        label="Продажи подписок БЧС"
                        value={metrics.subscription_returning_clients_count}
                        onChange={(value) => updateMetric('subscription_returning_clients_count', value)}
                        disabled={!canEdit}
                        {...countInputProps}
                      />
                      <NumberInput
                        label="Выручка"
                        value={metrics.subscription_returning_clients_amount}
                        onChange={(value) => updateMetricWithCalculation('subscription_returning_clients_amount', value)}
                        disabled={!canEdit}
                        inputMode="decimal"
                        min={0}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput
                        label="Членские взносы при переходах"
                        value={metrics.membership_fees_subscription_upgrades_amount}
                        onChange={(value) => updateMetricWithCalculation('membership_fees_subscription_upgrades_amount', value)}
                        disabled={!canEdit}
                        inputMode="decimal"
                        min={0}
                      />
                      <NumberInput
                        label="Членские взносы БЧС"
                        value={metrics.membership_fees_subscription_returning_amount}
                        onChange={(value) => updateMetricWithCalculation('membership_fees_subscription_returning_amount', value)}
                        disabled={!canEdit}
                        inputMode="decimal"
                        min={0}
                      />
                    </div>
                  </ChannelSubsection>
                </ChannelSection>
              )}
            </>
          ) : (
            <>
              <div>
                <h3 className="mb-3 text-base font-semibold text-muted-foreground">Новые клиенты</h3>
                <div className="space-y-4">
                  <NumberInput
                    label="Записи"
                    value={metrics.appointments_count}
                    onChange={(value) => updateMetric('appointments_count', value)}
                    disabled={!canEdit}
                    {...countInputProps}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <NumberInput
                      label="Приходы новых"
                      value={metrics.new_visitors_count}
                      onChange={(value) => updateMetric('new_visitors_count', value)}
                      disabled={!canEdit}
                      {...countInputProps}
                    />
                    <NumberInput
                      label="Выручка от пробных"
                      value={metrics.trial_payments_amount}
                      onChange={(value) => updateMetricWithCalculation('trial_payments_amount', value)}
                      disabled={!canEdit}
                      inputMode="decimal"
                      min={0}
                    />
                  </div>

                  {showRegularFields && (
                    <div className="grid grid-cols-2 gap-4">
                      <NumberInput
                        label="Продажи абонементов новым / доплаты"
                        value={metrics.new_sales_count}
                        onChange={(value) => updateMetric('new_sales_count', value)}
                        disabled={!canEdit}
                        {...countInputProps}
                      />
                      <NumberInput
                        label="Выручка по новым / доплатам"
                        value={metrics.new_sales_amount}
                        onChange={(value) => updateMetricWithCalculation('new_sales_amount', value)}
                        disabled={!canEdit}
                        inputMode="decimal"
                        min={0}
                      />
                    </div>
                  )}

                  {showSubscriptionFields && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <NumberInput
                          label="Новых подписок"
                          value={metrics.new_subscriptions_count}
                          onChange={(value) => updateMetric('new_subscriptions_count', value)}
                          disabled={!canEdit}
                          {...countInputProps}
                        />
                        <NumberInput
                          label="Выручка по подпискам"
                          value={metrics.new_subscriptions_amount}
                          onChange={(value) => updateMetricWithCalculation('new_subscriptions_amount', value)}
                          disabled={!canEdit}
                          inputMode="decimal"
                          min={0}
                        />
                      </div>
                      <NumberInput
                        label="Членские взносы новых клиентов"
                        value={metrics.membership_fees_new_amount}
                        onChange={(value) => updateMetricWithCalculation('membership_fees_new_amount', value)}
                        disabled={!canEdit}
                        inputMode="decimal"
                        min={0}
                      />
                    </>
                  )}
                </div>
              </div>

              <div>
                <h3 className="mb-3 text-base font-semibold text-muted-foreground">Действующие и бывшие клиенты</h3>
                <div className="space-y-4">
                  {showRegularFields && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <NumberInput
                          label="Продления абонементов"
                          value={metrics.renewals_count}
                          onChange={(value) => updateMetric('renewals_count', value)}
                          disabled={!canEdit}
                          {...countInputProps}
                        />
                        <NumberInput
                          label="Выручка"
                          value={metrics.renewals_amount}
                          onChange={(value) => updateMetricWithCalculation('renewals_amount', value)}
                          disabled={!canEdit}
                          inputMode="decimal"
                          min={0}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <NumberInput
                          label="Апгрейды абонементов"
                          value={metrics.upgrades_count}
                          onChange={(value) => updateMetric('upgrades_count', value)}
                          disabled={!canEdit}
                          {...countInputProps}
                        />
                        <NumberInput
                          label="Выручка"
                          value={metrics.upgrades_amount}
                          onChange={(value) => updateMetricWithCalculation('upgrades_amount', value)}
                          disabled={!canEdit}
                          inputMode="decimal"
                          min={0}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <NumberInput
                          label="Продажи абонементов БЧС"
                          value={metrics.returning_clients_count}
                          onChange={(value) => updateMetric('returning_clients_count', value)}
                          disabled={!canEdit}
                          {...countInputProps}
                        />
                        <NumberInput
                          label="Выручка"
                          value={metrics.returning_clients_amount}
                          onChange={(value) => updateMetricWithCalculation('returning_clients_amount', value)}
                          disabled={!canEdit}
                          inputMode="decimal"
                          min={0}
                        />
                      </div>
                    </>
                  )}

                  {showSubscriptionFields && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <NumberInput
                          label="Автопродления подписок"
                          value={metrics.subscription_renewals_count}
                          onChange={(value) => updateMetric('subscription_renewals_count', value)}
                          disabled={!canEdit}
                          {...countInputProps}
                        />
                        <NumberInput
                          label="Выручка"
                          value={metrics.subscription_renewals_amount}
                          onChange={(value) => updateMetricWithCalculation('subscription_renewals_amount', value)}
                          disabled={!canEdit}
                          inputMode="decimal"
                          min={0}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <NumberInput
                          label="Переходов с абонемента на подписку"
                          value={metrics.subscription_upgrades_count}
                          onChange={(value) => updateMetric('subscription_upgrades_count', value)}
                          disabled={!canEdit}
                          {...countInputProps}
                        />
                        <NumberInput
                          label="Выручка"
                          value={metrics.subscription_upgrades_amount}
                          onChange={(value) => updateMetricWithCalculation('subscription_upgrades_amount', value)}
                          disabled={!canEdit}
                          inputMode="decimal"
                          min={0}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <NumberInput
                          label="Продажи подписок БЧС"
                          value={metrics.subscription_returning_clients_count}
                          onChange={(value) => updateMetric('subscription_returning_clients_count', value)}
                          disabled={!canEdit}
                          {...countInputProps}
                        />
                        <NumberInput
                          label="Выручка"
                          value={metrics.subscription_returning_clients_amount}
                          onChange={(value) => updateMetricWithCalculation('subscription_returning_clients_amount', value)}
                          disabled={!canEdit}
                          inputMode="decimal"
                          min={0}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <NumberInput
                          label="Членские взносы при переходах"
                          value={metrics.membership_fees_subscription_upgrades_amount}
                          onChange={(value) => updateMetricWithCalculation('membership_fees_subscription_upgrades_amount', value)}
                          disabled={!canEdit}
                          inputMode="decimal"
                          min={0}
                        />
                        <NumberInput
                          label="Членские взносы БЧС"
                          value={metrics.membership_fees_subscription_returning_amount}
                          onChange={(value) => updateMetricWithCalculation('membership_fees_subscription_returning_amount', value)}
                          disabled={!canEdit}
                          inputMode="decimal"
                          min={0}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Индивидуальные занятия и прочее */}
          <div>
            <h3 className="mb-3 text-base font-semibold text-muted-foreground">Индивидуальные занятия и прочее</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <NumberInput
                  label="Блоки индивов"
                  value={metrics.private_sessions_count}
                  onChange={(value) => updateMetric('private_sessions_count', value)}
                  disabled={!canEdit}
                  min={0}
                />
                <NumberInput
                  label="Выручка индивы"
                  value={metrics.private_sessions_amount}
                  onChange={(value) => updateMetricWithCalculation('private_sessions_amount', value)}
                  disabled={!canEdit}
                  inputMode="decimal"
                  min={0}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <NumberInput
                  label="Прочая выручка"
                  value={metrics.other_income}
                  onChange={(value) => updateMetricWithCalculation('other_income', value)}
                  disabled={!canEdit}
                  inputMode="decimal"
                />
                <RefundsInput
                  label="Возвраты"
                  value={metrics.refunds_amount}
                  onChange={(value) => updateMetricWithCalculation('refunds_amount', value)}
                  disabled={!canEdit}
                />
              </div>
            </div>
          </div>

          {shouldShowRegularSalesToggle && (
            <div className={`rounded-xl border px-4 py-3 transition-colors ${
              showRegularSalesBlock
                ? 'border-amber-300 bg-amber-50/70 shadow-sm'
                : 'border-amber-200 bg-amber-50/30 hover:border-amber-300 hover:bg-amber-50/50'
            }`}>
              <button
                type="button"
                onClick={() => setShowRegularSalesBlock((prev) => !prev)}
                className="flex w-full items-center justify-between gap-4 text-left transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-sm font-semibold text-amber-900">
                      Доплаты по обычным абонементам
                    </span>
                    <span
                      className="inline-flex h-5 w-5 items-center justify-center rounded-full text-amber-800/80"
                      title="Используйте этот блок для продаж и доплат по обычным абонементам, если их нужно отдельно внести в показатели за день."
                      aria-label="Информация о блоке продаж обычных абонементов"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-amber-900/60">
                    {showRegularSalesBlock ? 'Скрыть поля' : 'Нажмите, если нужно добавить'}
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-0.5 text-amber-900">
                  {regularSalesSummary && (
                    <span className="hidden rounded-full border border-amber-300 bg-card/70 px-2 py-0.5 text-[11px] font-medium sm:inline-flex">
                      {regularSalesSummary}
                    </span>
                  )}
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-amber-300 bg-card/85 transition-transform ${
                    showRegularSalesBlock ? 'rotate-180' : ''
                  }`}>
                    <ChevronDown className="h-4 w-4" />
                  </span>
                </div>
              </button>

              {showRegularSalesBlock && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-card/70 p-4">
                  <div className="space-y-5">
                    <div>
                      <h3 className="mb-3 text-sm font-semibold text-amber-900">Новые клиенты</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <NumberInput
                          label="Количество доплат за абонементы новым клиентам"
                          value={metrics.new_sales_count}
                          onChange={(value) => updateMetric('new_sales_count', value)}
                          disabled={!canEdit}
                          {...countInputProps}
                        />
                        <NumberInput
                          label="Выручка"
                          value={metrics.new_sales_amount}
                          onChange={(value) => updateMetricWithCalculation('new_sales_amount', value)}
                          disabled={!canEdit}
                          inputMode="decimal"
                          min={0}
                        />
                      </div>
                    </div>

                    <div>
                      <h3 className="mb-3 text-sm font-semibold text-amber-900">Действующие и бывшие клиенты</h3>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <NumberInput
                            label="Количество доплат за продление абонементов"
                            value={metrics.renewals_count}
                            onChange={(value) => updateMetric('renewals_count', value)}
                            disabled={!canEdit}
                            {...countInputProps}
                          />
                          <NumberInput
                            label="Выручка"
                            value={metrics.renewals_amount}
                            onChange={(value) => updateMetricWithCalculation('renewals_amount', value)}
                            disabled={!canEdit}
                            inputMode="decimal"
                            min={0}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <NumberInput
                            label="Количество доплат за апгрейд абонементов"
                            value={metrics.upgrades_count}
                            onChange={(value) => updateMetric('upgrades_count', value)}
                            disabled={!canEdit}
                            {...countInputProps}
                          />
                          <NumberInput
                            label="Выручка"
                            value={metrics.upgrades_amount}
                            onChange={(value) => updateMetricWithCalculation('upgrades_amount', value)}
                            disabled={!canEdit}
                            inputMode="decimal"
                            min={0}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <NumberInput
                            label="Количество доплат за абонементы БЧС"
                            value={metrics.returning_clients_count}
                            onChange={(value) => updateMetric('returning_clients_count', value)}
                            disabled={!canEdit}
                            {...countInputProps}
                          />
                          <NumberInput
                            label="Выручка"
                            value={metrics.returning_clients_amount}
                            onChange={(value) => updateMetricWithCalculation('returning_clients_amount', value)}
                            disabled={!canEdit}
                            inputMode="decimal"
                            min={0}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Итого выручка за день */}
          <div>
            <h3 className="mb-3 text-base font-semibold text-muted-foreground">Итого выручка за день</h3>
            <div className="relative">
              <NumberInput
                value={metrics.gross_revenue}
                disabled={true}
                min={0}
              />
              {/* Индикатор статуса подтверждения */}
              {(originalMetrics.gross_revenue || 0) > 0 && (
                <div className="absolute top-0 right-0 -mt-1 -mr-1">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                    metrics.gross_revenue_confirmed
                      ? 'bg-green-100 text-green-800 border-green-200'
                      : 'bg-amber-100 text-amber-800 border-amber-200'
                  }`}>
                    {metrics.gross_revenue_confirmed ? (
                      <>
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Подтверждено
                      </>
                    ) : (
                      <>
                        <Clock className="w-3 h-3 mr-1" />
                        Не подтверждено
                      </>
                    )}
                  </span>
                </div>
              )}
            </div>
            
            {/* Кем и когда: сохранение и подтверждение дня */}
            {(savedAuthorLabel || hasConfirmedAuthorship) && (
              <div className="mt-2 space-y-1">
                {savedAndConfirmedTogether ? (
                  <div className="p-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-800">
                    ✅ Сохранил и подтвердил: <span className="font-medium">{savedAuthorLabel}</span>
                    {savedAtLabel && <> · {savedAtLabel}</>}
                  </div>
                ) : (
                  <>
                    {savedAuthorLabel && (
                      <div className="p-2 bg-muted/30 border border-border rounded-lg text-xs text-muted-foreground">
                        💾 Сохранил: <span className="font-medium text-foreground">{savedAuthorLabel}</span>
                        {savedAtLabel && <> · {savedAtLabel}</>}
                      </div>
                    )}
                    {hasConfirmedAuthorship && (
                      <div className="p-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-800">
                        {/* Имени может не быть: до перехода на uuid в confirmed_by
                            писали telegram_id, а на окружении без RPC авторства
                            известна только дата из колонки. */}
                        ✅ {confirmedAuthorLabel
                          ? <>Подтвердил: <span className="font-medium">{confirmedAuthorLabel}</span></>
                          : 'Подтверждено'}
                        {confirmedAtLabel && <> · {confirmedAtLabel}</>}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Детализация расчета */}
            {shouldShowRevenueComparison && (
              <div className="mt-3 p-3 bg-muted/20 rounded-lg border border-muted">
                <div className="text-xs text-muted-foreground mb-2 font-medium">Детализация расчета:</div>
                <div className="space-y-1 text-xs tabular-nums">
                  {(metrics.new_sales_amount || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Новые продажи:</span>
                      <span>+{formatAmountWithSpaces(metrics.new_sales_amount!)}</span>
                    </div>
                  )}
                  {(metrics.trial_payments_amount || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Выручка от пробных:</span>
                      <span>+{formatAmountWithSpaces(metrics.trial_payments_amount!)}</span>
                    </div>
                  )}
                  {(metrics.new_subscriptions_amount || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Новые подписки:</span>
                      <span>+{formatAmountWithSpaces(metrics.new_subscriptions_amount!)}</span>
                    </div>
                  )}
                  {(metrics.membership_fees_new_amount || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Членские взносы новых клиентов:</span>
                      <span>+{formatAmountWithSpaces(metrics.membership_fees_new_amount!)}</span>
                    </div>
                  )}
                  {(metrics.renewals_amount || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Продления абонементов:</span>
                      <span>+{formatAmountWithSpaces(metrics.renewals_amount!)}</span>
                    </div>
                  )}
                  {(metrics.upgrades_amount || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Апгрейды абонементов:</span>
                      <span>+{formatAmountWithSpaces(metrics.upgrades_amount!)}</span>
                    </div>
                  )}
                  {(metrics.returning_clients_amount || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Продажи абонементов БЧС:</span>
                      <span>+{formatAmountWithSpaces(metrics.returning_clients_amount!)}</span>
                    </div>
                  )}
                  {(metrics.subscription_renewals_amount || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Автопродления подписок:</span>
                      <span>+{formatAmountWithSpaces(metrics.subscription_renewals_amount!)}</span>
                    </div>
                  )}
                  {(metrics.subscription_upgrades_amount || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Переходы с абонемента на подписку:</span>
                      <span>+{formatAmountWithSpaces(metrics.subscription_upgrades_amount!)}</span>
                    </div>
                  )}
                  {(metrics.subscription_returning_clients_amount || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Продажи подписок БЧС:</span>
                      <span>+{formatAmountWithSpaces(metrics.subscription_returning_clients_amount!)}</span>
                    </div>
                  )}
                  {(metrics.membership_fees_subscription_upgrades_amount || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Членские взносы при переходах:</span>
                      <span>+{formatAmountWithSpaces(metrics.membership_fees_subscription_upgrades_amount!)}</span>
                    </div>
                  )}
                  {(metrics.membership_fees_subscription_returning_amount || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Членские взносы БЧС:</span>
                      <span>+{formatAmountWithSpaces(metrics.membership_fees_subscription_returning_amount!)}</span>
                    </div>
                  )}
                  {(metrics.private_sessions_amount || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Индивидуальные:</span>
                      <span>+{formatAmountWithSpaces(metrics.private_sessions_amount!)}</span>
                    </div>
                  )}
                  {(metrics.other_income || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Прочие:</span>
                      <span>+{formatAmountWithSpaces(metrics.other_income!)}</span>
                    </div>
                  )}
                  {(metrics.refunds_amount || 0) !== 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Возвраты:</span>
                      <span>{formatAmountWithSpaces(metrics.refunds_amount!)}</span>
                    </div>
                  )}
                  {shouldShowRevenueSplit && (
                    <div className="border-t border-muted pt-2 mt-3 space-y-1">
                      <div className="flex justify-between">
                        <span>Итого по абонементам:</span>
                        <span>{formatAmountWithSpaces(appRevenueSplitForSelectedDate.memberships)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Итого по подпискам:</span>
                        <span>{formatAmountWithSpaces(appRevenueSplitForSelectedDate.subscriptions)}</span>
                      </div>
                    </div>
                  )}
                  <div className={`flex justify-between items-baseline font-semibold text-sm ${shouldShowRevenueSplit ? 'pt-1' : 'border-t border-muted pt-1 mt-3'}`}>
                    <span>Итого:</span>
                    <span className="font-bold tracking-[0.01em]">{formatAmountWithSpaces(appRevenueForSelectedDate)}</span>
                  </div>
                  {(hasCrmForSelectedDate && crmRevenueForSelectedDate !== null) && (
                    <>
                      {shouldShowRevenueSplit && (
                        <div className="pt-3 mt-2 space-y-1">
                          <div className="flex justify-between">
                            <span>Listok CRM по абонементам:</span>
                            <span>{formatAmountWithSpaces(crmRevenueSplitForSelectedDate.memberships)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Listok CRM по подпискам:</span>
                            <span>{formatAmountWithSpaces(crmRevenueSplitForSelectedDate.subscriptions)}</span>
                          </div>
                        </div>
                      )}
                      <div className={`flex justify-between items-baseline font-semibold text-sm ${shouldShowRevenueSplit ? 'pt-1' : 'pt-3 mt-2'}`}>
                        <span>Итого Listok CRM:</span>
                        <span className="font-bold tracking-[0.01em]">{formatAmountWithSpaces(crmRevenueForSelectedDate)}</span>
                      </div>
                    </>
                  )}
                  {(!hasCrmForSelectedDate || crmRevenueForSelectedDate === null) && (
                    <div className={`flex justify-between items-baseline font-semibold text-sm ${shouldShowRevenueSplit ? 'pt-1' : 'pt-3 mt-2'}`}>
                      <span>Итого Listok CRM:</span>
                      <span className="font-bold tracking-[0.01em]">{formatAmountWithSpaces(0)}</span>
                    </div>
                  )}
                  {shouldShowRevenueComparison && (
                    <div className={`flex justify-between ${crmDeltaForSelectedDate === 0 ? 'text-green-700' : 'text-rose-700'}`}>
                      <span>Отклонение от CRM:</span>
                      <span>{crmDeltaForSelectedDate >= 0 ? '+' : ''}{formatAmountWithSpaces(crmDeltaForSelectedDate)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {canEdit && (
          <div className="mt-6 pt-4 border-t border-border">
            {saveSuccess && (
              <div className="mb-4 space-y-2">
                <div className="p-3 bg-green-100 border border-green-300 rounded-lg text-green-800 text-sm text-center">
                  {saveConfirmsRevenue
                    ? '✅ Данные сохранены, выручка подтверждена'
                    : '✅ Данные успешно сохранены!'}
                </div>
                {/* Сброс подтверждения — следствие сохранения НЕ владельцем
                    (см. saveConfirmsRevenue): владельцу сохранение, наоборот,
                    подтверждение проставляет. */}
                {!saveConfirmsRevenue && hasDataToConfirm && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
                    <div className="font-medium mb-1">⚠️ Требуется новое подтверждение</div>
                    <div className="text-xs">
                      После изменения данных статус подтверждения сброшен. Подтвердить выручку может владелец.
                    </div>
                  </div>
                )}
              </div>
            )}
            
          </div>
        )}
      </Card>

      {saving && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-4 text-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p>Сохранение...</p>
          </div>
        </div>
      )}

      {/* Модальное окно массового расхода на рекламу */}
      <BulkMetricModal
        isOpen={showBulkMarketingModal}
        onClose={() => setShowBulkMarketingModal(false)}
        onSaved={handleBulkMetricSaved('marketing_expenses_amount')}
        studios={studios}
        selectedDate={selectedDate}
        userId={user?.id ?? null}
        currentStudioId={studioIntId}
        currentPartnerNumber={currentPartnerNumber}
        currentPeriodId={selectedPeriod?.period_id ?? null}
        dbField="marketing_expenses_amount"
        title="Расход на несколько студий"
        totalLabel="Распределяемая сумма"
        currency={studioCurrency}
      />

      {/* Модальное окно массового ввода лидов */}
      <BulkMetricModal
        isOpen={showBulkLeadsModal}
        onClose={() => setShowBulkLeadsModal(false)}
        onSaved={handleBulkMetricSaved('leads_count')}
        studios={studios}
        selectedDate={selectedDate}
        userId={user?.id ?? null}
        currentStudioId={studioIntId}
        currentPartnerNumber={currentPartnerNumber}
        currentPeriodId={selectedPeriod?.period_id ?? null}
        dbField="leads_count"
        title="Лиды на несколько студий"
        totalLabel="Всего лидов"
      />

      {/* Модальное окно подтверждения выручки */}
      {showRevenueConfirmation && (
        <RevenueConfirmation
          metrics={metrics as DailyTotals}
          selectedDate={selectedDate}
          crmRevenue={crmRevenueForSelectedDate}
          currency={studioCurrency}
          onConfirm={async () => { await handleConfirmRevenue() }}
          onCancel={() => setShowRevenueConfirmation(false)}
        />
      )}
    </div>
  )
}
